import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, Users, FileText, MapPin, Star, Plus, Trash2, Edit, 
  Save, X, Lock, BarChart3, Mail, Check, Clock, ArrowLeft,
  ThumbsUp, ThumbsDown, UserCheck, Briefcase, Send, ChevronRight,
  AlertCircle, Eye, CreditCard, Link, ExternalLink, TrendingUp,
  DollarSign, PieChart, Activity, Video, Layers, Film,
  GripVertical, ChevronDown, ChevronUp, Monitor, Smartphone, Square, RectangleVertical, RefreshCw,
  Tag, Settings, Link2, User, UserPlus, MessageCircle, HelpCircle, CheckCircle,
  Globe, Zap, Palette, Sliders, Download, Building2, Receipt, Hash, Phone, Bell, BellRing, Volume2, VolumeX, Calculator
} from 'lucide-react';
import ChatWidget from '../components/ChatWidget';
import { toast, Toaster } from '../components/ui/sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginMode, setLoginMode] = useState('password'); // 'password' or 'email'
  const [adminUser, setAdminUser] = useState(null);
  const [activeTab, setActiveTabState] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    return hash || 'review';
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    window.location.hash = tab;
  };
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Data states
  const [teams, setTeams] = useState([]);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [freelancerApplications, setFreelancerApplications] = useState([]);
  const [freelancerUsers, setFreelancerUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  
  // Invoices
  const [allInvoices, setAllInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [invoiceSubTab, setInvoiceSubTab] = useState('list'); // 'list' or 'settings'
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [loadingInvoiceSettings, setLoadingInvoiceSettings] = useState(false);
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  
  // Tinder-style request review
  const [currentRequestIndex, setCurrentRequestIndex] = useState(0);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedFreelancer, setSelectedFreelancer] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [reviewCardExpanded, setReviewCardExpanded] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState({});
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeStartX, setSwipeStartX] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  // Freelancer creation modal
  const [showCreateFreelancer, setShowCreateFreelancer] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [freelancerPassword, setFreelancerPassword] = useState('');
  const [createAccountLoading, setCreateAccountLoading] = useState(false);

  // Notification bell
  const [notifications, setNotifications] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem('revocut_notif_sound');
    return stored !== 'false';
  });
  const prevRequestCountRef = React.useRef(null);
  const prevApplicationCountRef = React.useRef(null);
  const notifBellRef = React.useRef(null);

  // Tags management
  const [tagCategories, setTagCategories] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [newTagInput, setNewTagInput] = useState({});
  const [createAccountError, setCreateAccountError] = useState('');
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedJobForPayment, setSelectedJobForPayment] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [generatedPaymentLink, setGeneratedPaymentLink] = useState(null);
  
  // Platform margin settings (RevoCut Marge)
  const [marginTiers, setMarginTiers] = useState([]);
  const [marginLoading, setMarginLoading] = useState(false);
  
  // Pricing configuration state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [editingVideoType, setEditingVideoType] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [newVideoType, setNewVideoType] = useState({ name: '', factor: '1.0', description: '', icon: 'video' });
  const [newAddon, setNewAddon] = useState({ name: '', percentage: 10, description: '' });
  
  // New Pricing states for categories
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [showNewAddonModal, setShowNewAddonModal] = useState(false);
  const [selectedCategoryForAddon, setSelectedCategoryForAddon] = useState(null);
  const [newCategoryAddon, setNewCategoryAddon] = useState({ 
    name: '', description: '', percentage: null, flat_price: null, price_type: 'percentage' 
  });
  const [pricingView, setPricingView] = useState('admin'); // 'admin' or 'freelancer' (for RevoCut)
  const [editingFormat, setEditingFormat] = useState(null);
  const [newFormat, setNewFormat] = useState({ name: '', description: '', icon: 'monitor' });
  const [draggedItem, setDraggedItem] = useState(null);
  
  // Debounce refs for pricing updates
  const pricingDebounceRef = useRef(null);
  const pricingSaveRef = useRef(null);
  
  // Live Preview state
  const [showPreview, setShowPreview] = useState(false);
  const [previewVideo, setPreviewVideo] = useState({
    format: '16_9',
    videoType: 'vlog',
    language: 'de',
    subtitleType: 'none',
    subtitleColor: '#FFFFFF',
    videoCount: 1,
    minutesPerVideo: 3,
    rawMaterialMinutes: 30,
    needsBRoll: false,
    rushDelivery: 'none',
    extraRevisions: 'none',
    addons: {}
  });
  
  // Helper: update pricingConfig locally and debounce API call (prevents scroll jump)
  const updatePricingField = (updater, apiCall) => {
    setPricingConfig(prev => {
      const updated = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      return updated;
    });
    clearTimeout(pricingDebounceRef.current);
    pricingDebounceRef.current = setTimeout(async () => {
      try { await apiCall(); } catch (e) { console.error('Pricing update error:', e); }
    }, 600);
  };
  
  // FAQ Chatbot states
  const [faqs, setFaqs] = useState([]);
  const [chatbotSettings, setChatbotSettings] = useState({
    welcome_message: 'Hallo! 👋 Wie kann ich dir helfen?',
    fallback_message: 'Das kann ich leider nicht beantworten. Schreib uns gerne eine E-Mail an info@revocut-media.de',
    email_prompt: 'Für weitere Fragen erreichst du uns unter info@revocut-media.de',
    is_active: true,
    show_on_pages: ['home', 'calculator', 'freelancer']
  });
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({ 
    question: '', 
    answer: '', 
    keywords: '', 
    category: 'Allgemein',
    is_active: true 
  });
  const [loadingFaqs, setLoadingFaqs] = useState(false);
  
  // Form states
  const [editingTeam, setEditingTeam] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [teamForm, setTeamForm] = useState({
    name: '',
    type: 'freelancer',
    description: '',
    location: { city: '', lat: null, lng: null },
    avatar: '',
    is_boosted: false,
    is_premium: false,
    email: '',
    phone: '',
    specializations: [],
    software_skills: [],
    portfolio_links: [],
    social_links: {
      instagram: '',
      youtube: '',
      twitter: '',
      website: ''
    },
    prices: {
      basePerMinute: 80,
      motionDesign: 100,
      colorGrading: 30,
      musicLicensing: 50,
      soundDesign: 40,
      subtitles: 20,
      thumbnails: 25,
    }
  });
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newSoftwareSkill, setNewSoftwareSkill] = useState('');
  const [newPortfolioLink, setNewPortfolioLink] = useState({ title: '', url: '' });

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      let response;
      
      if (loginMode === 'email') {
        // Email-based login
        response = await fetch(`${API_URL}/api/admin/login-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password })
        });
        
        if (response.ok) {
          const data = await response.json();
          setIsAuthenticated(true);
          setAdminUser(data.admin);
          localStorage.setItem('revocut_admin', JSON.stringify(data.admin));
          localStorage.setItem('revocut_admin_token', data.token);
          fetchData(); // Fetch data immediately after login
        } else {
          const error = await response.json();
          setLoginError(error.detail || 'Login fehlgeschlagen');
        }
      } else {
        // Simple password login (legacy)
        response = await fetch(`${API_URL}/api/admin/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        });
        
        if (response.ok) {
          setIsAuthenticated(true);
          localStorage.setItem('revocut_admin', 'true');
          fetchData(); // Fetch data immediately after login
          fetchMarginSettings();
          fetchPricingConfig();
        } else {
          setLoginError('Falsches Passwort');
        }
      }
    } catch (error) {
      setLoginError('Verbindungsfehler');
    }
  };

  // Check auth on mount
  useEffect(() => {
    const isAdmin = localStorage.getItem('revocut_admin');
    if (isAdmin) {
      setIsAuthenticated(true);
      if (isAdmin !== 'true') {
        try {
          setAdminUser(JSON.parse(isAdmin));
        } catch {}
      }
      fetchData();
      fetchMarginSettings();
      fetchFaqs();
      fetchChatbotSettings();
    }
  }, []);

  // Auto-refresh: Poll for new data every 15 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      fetchData(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // --- Notification Bell Logic ---
  const playNotifSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.08);
      osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.16);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } catch (e) {}
  }, [soundEnabled]);

  const addNotification = useCallback((text, type = 'request') => {
    const notif = { id: Date.now(), text, type, time: new Date(), read: false };
    setNotifications(prev => [notif, ...prev].slice(0, 20));
  }, []);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('revocut_notif_sound', String(next));
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (notifBellRef.current && !notifBellRef.current.contains(e.target)) {
        setShowNotifDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Check for new requests/applications after each fetchData
  useEffect(() => {
    if (!isAuthenticated || (!requests.length && !freelancerApplications.length)) return;

    const newReqCount = requests.filter(r => r.status === 'new').length;
    const newAppCount = freelancerApplications.filter(a => a.status === 'pending').length;

    if (prevRequestCountRef.current !== null && newReqCount > prevRequestCountRef.current) {
      const diff = newReqCount - prevRequestCountRef.current;
      addNotification(`${diff} neue Anfrage${diff > 1 ? 'n' : ''} eingegangen!`, 'request');
      playNotifSound();
    }

    if (prevApplicationCountRef.current !== null && newAppCount > prevApplicationCountRef.current) {
      const diff = newAppCount - prevApplicationCountRef.current;
      addNotification(`${diff} neue Bewerbung${diff > 1 ? 'en' : ''} eingegangen!`, 'application');
      playNotifSound();
    }

    prevRequestCountRef.current = newReqCount;
    prevApplicationCountRef.current = newAppCount;
  }, [requests, freelancerApplications, isAuthenticated, addNotification, playNotifSound]);

  const fetchAllInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/all`);
      if (res.ok) setAllInvoices(await res.json());
    } catch (e) {
      console.error('Error fetching invoices:', e);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const fetchInvoiceSettings = async () => {
    setLoadingInvoiceSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/invoice-settings`);
      if (res.ok) setInvoiceSettings(await res.json());
    } catch (e) {
      console.error('Error fetching invoice settings:', e);
    } finally {
      setLoadingInvoiceSettings(false);
    }
  };

  // Tags management functions
  const fetchTagCategories = async () => {
    setLoadingTags(true);
    try {
      const res = await fetch(`${API_URL}/api/explore/tag-categories`);
      if (res.ok) setTagCategories(await res.json());
    } catch (e) {
      console.error('Error fetching tags:', e);
    } finally {
      setLoadingTags(false);
    }
  };

  const saveTagCategories = async () => {
    setSavingTags(true);
    try {
      const res = await fetch(`${API_URL}/api/explore/tag-categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tagCategories)
      });
      if (res.ok) toast.success('Tags gespeichert');
      else toast.error('Fehler beim Speichern');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingTags(false);
    }
  };

  const addTagCategory = () => {
    const name = prompt('Name der neuen Kategorie:');
    if (!name) return;
    const id = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setTagCategories(prev => [...prev, { id, name, tags: [] }]);
  };

  const removeTagCategory = (catId) => {
    if (!window.confirm('Kategorie löschen?')) return;
    setTagCategories(prev => prev.filter(c => c.id !== catId));
  };

  const addTagToCategory = (catId) => {
    const tagName = (newTagInput[catId] || '').trim();
    if (!tagName) return;
    setTagCategories(prev => prev.map(c =>
      c.id === catId ? { ...c, tags: [...(c.tags || []), tagName] } : c
    ));
    setNewTagInput(prev => ({ ...prev, [catId]: '' }));
  };

  const removeTagFromCategory = (catId, tagName) => {
    setTagCategories(prev => prev.map(c =>
      c.id === catId ? { ...c, tags: (c.tags || []).filter(t => t !== tagName) } : c
    ));
  };

  const saveInvoiceSettings = async () => {
    if (!invoiceSettings) return;
    setSavingInvoiceSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/invoice-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceSettings)
      });
      if (res.ok) toast.success('Rechnungseinstellungen gespeichert');
      else toast.error('Fehler beim Speichern');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  const handleViewInvoiceDetail = async (invoiceId) => {
    setLoadingInvoiceDetail(true);
    setShowInvoiceDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/detail`);
      if (res.ok) {
        const data = await res.json();
        setSelectedInvoice(data);
      } else {
        toast.error('Rechnung nicht gefunden');
        setShowInvoiceDetail(false);
      }
    } catch (e) {
      toast.error('Fehler beim Laden der Rechnung');
      setShowInvoiceDetail(false);
    } finally {
      setLoadingInvoiceDetail(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung_${invoiceNumber || invoiceId.slice(-6)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF heruntergeladen');
      } else {
        toast.error('Fehler beim PDF-Download');
      }
    } catch (e) {
      toast.error('Fehler beim PDF-Download');
    }
  };

  const handleDownloadXML = async (invoiceId, invoiceNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/xml`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `XRechnung_${invoiceNumber || invoiceId.slice(-6)}.xml`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('XRechnung-XML heruntergeladen');
      } else {
        toast.error('Fehler beim XML-Download');
      }
    } catch (e) {
      toast.error('Fehler beim XML-Download');
    }
  };

  const handleAssignInvoiceNumber = async (invoiceId) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${invoiceId}/assign-number`, { method: 'PUT' });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Rechnungsnummer ${data.invoice_number} zugewiesen`);
        fetchAllInvoices();
        return data.invoice_number;
      }
    } catch (e) {
      toast.error('Fehler beim Zuweisen der Nummer');
    }
    return null;
  };

  const handleSendInvoiceToCustomer = async (invoice) => {
    if (!invoice?.job_id) {
      toast.error('Kein Job zugeordnet - Rechnung kann nicht gesendet werden');
      return;
    }
    const stripeLink = invoice.stripe_payment_link || invoice.payment_link;
    if (!stripeLink) {
      toast.error('Kein Zahlungslink vorhanden. Bitte zuerst einen Stripe-Zahlungslink erstellen.');
      return;
    }
    if (!window.confirm(`Rechnung an ${invoice.customer_email || 'Kunde'} senden?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/payments/send-invoice?job_id=${invoice.job_id}&payment_link=${encodeURIComponent(stripeLink)}`, { method: 'POST' });
      if (res.ok) {
        toast.success('Rechnung erfolgreich per E-Mail gesendet!');
        fetchAllInvoices();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Senden');
      }
    } catch (e) {
      toast.error('Fehler beim Senden der Rechnung');
    }
  };


  // Fetch all data
  const fetchData = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const [teamsRes, requestsRes, statsRes, applicationsRes, freelancersRes, jobsRes] = await Promise.all([
        fetch(`${API_URL}/api/teams`),
        fetch(`${API_URL}/api/requests`),
        fetch(`${API_URL}/api/admin/stats`),
        fetch(`${API_URL}/api/freelancer-applications`),
        fetch(`${API_URL}/api/freelancer/users`),
        fetch(`${API_URL}/api/jobs/all`)
      ]);
      
      if (teamsRes.ok) setTeams(await teamsRes.json());
      if (requestsRes.ok) setRequests(await requestsRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (applicationsRes.ok) setFreelancerApplications(await applicationsRes.json());
      if (freelancersRes.ok) setFreelancerUsers(await freelancersRes.json());
      if (jobsRes.ok) setJobs(await jobsRes.json());
      
      // Also fetch analytics
      fetchAnalytics();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async (period = analyticsPeriod) => {
    setLoadingAnalytics(true);
    try {
      const response = await fetch(`${API_URL}/api/analytics/admin?period=${period}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setAnalyticsPeriod(newPeriod);
    fetchAnalytics(newPeriod);
  };

  // Fetch platform margin settings
  const fetchMarginSettings = async () => {
    setMarginLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/platform-settings`);
      if (response.ok) {
        const data = await response.json();
        console.log('Margin data loaded:', data);
        // Always set margin tiers from response
        setMarginTiers(data.margin_tiers || [
          { min_amount: 0, margin_percent: 25, description: 'Bis €500' },
          { min_amount: 500, margin_percent: 20, description: '€500 - €1000' },
          { min_amount: 1000, margin_percent: 15, description: '€1000 - €2500' },
          { min_amount: 2500, margin_percent: 12, description: '€2500 - €5000' },
          { min_amount: 5000, margin_percent: 10, description: 'Ab €5000' },
        ]);
      } else {
        // Set defaults if API fails
        setMarginTiers([
          { min_amount: 0, margin_percent: 25, description: 'Bis €500' },
          { min_amount: 500, margin_percent: 20, description: '€500 - €1000' },
        ]);
      }
    } catch (error) {
      console.error('Error fetching margin settings:', error);
      // Set defaults on error
      setMarginTiers([
        { min_amount: 0, margin_percent: 25, description: 'Bis €500' },
        { min_amount: 500, margin_percent: 20, description: '€500 - €1000' },
      ]);
    } finally {
      setMarginLoading(false);
    }
  };

  // Save platform margin settings
  const saveMarginSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platform-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ margin_tiers: marginTiers })
      });
      if (response.ok) {
        toast.success('RevoCut Marge gespeichert!');
      }
    } catch (error) {
      console.error('Error saving margin settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Add new margin tier
  const addMarginTier = () => {
    const lastTier = marginTiers[marginTiers.length - 1];
    const newMinAmount = lastTier ? lastTier.min_amount + 500 : 0;
    setMarginTiers([...marginTiers, { min_amount: newMinAmount, margin_percent: 15, description: '' }]);
  };

  // Remove margin tier
  const removeMarginTier = (index) => {
    setMarginTiers(marginTiers.filter((_, i) => i !== index));
  };

  // Update margin tier
  const updateMarginTier = (index, field, value) => {
    const updated = [...marginTiers];
    updated[index] = { ...updated[index], [field]: value };
    setMarginTiers(updated);
  };

  // Fetch pricing configuration
  const fetchPricingConfig = async (silent = false) => {
    if (!silent) setLoadingPricing(true);
    try {
      const response = await fetch(`${API_URL}/api/pricing/config`);
      if (response.ok) {
        const data = await response.json();
        setPricingConfig(data);
      }
    } catch (error) {
      console.error('Error fetching pricing config:', error);
    } finally {
      if (!silent) setLoadingPricing(false);
    }
  };

  // Fetch FAQs
  const fetchFaqs = async () => {
    setLoadingFaqs(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/faq`);
      const data = await res.json();
      setFaqs(data);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoadingFaqs(false);
    }
  };

  // Fetch chatbot settings
  const fetchChatbotSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chatbot/settings`);
      const data = await res.json();
      setChatbotSettings(data);
    } catch (error) {
      console.error('Error fetching chatbot settings:', error);
    }
  };

  // Save FAQ
  const handleSaveFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast.error('Frage und Antwort sind erforderlich');
      return;
    }
    
    try {
      const faqData = {
        ...newFaq,
        keywords: newFaq.keywords.split(',').map(k => k.trim()).filter(k => k)
      };
      
      if (editingFaq) {
        await fetch(`${API_URL}/api/admin/faq/${editingFaq.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(faqData)
        });
        toast.success('FAQ aktualisiert');
      } else {
        await fetch(`${API_URL}/api/admin/faq`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(faqData)
        });
        toast.success('FAQ erstellt');
      }
      
      setShowFaqModal(false);
      setEditingFaq(null);
      setNewFaq({ question: '', answer: '', keywords: '', category: 'Allgemein', is_active: true });
      fetchFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Delete FAQ
  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('FAQ wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/admin/faq/${faqId}`, { method: 'DELETE' });
      toast.success('FAQ gelöscht');
      fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Save chatbot settings
  const handleSaveChatbotSettings = async () => {
    try {
      await fetch(`${API_URL}/api/admin/chatbot/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chatbotSettings)
      });
      toast.success('Chatbot-Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving chatbot settings:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  // Add video type
  const handleAddVideoType = async () => {
    if (!newVideoType.name) return;
    try {
      const response = await fetch(`${API_URL}/api/pricing/config/video-type`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newVideoType,
          factor: parseFloat(newVideoType.factor) || 1.0
        })
      });
      if (response.ok) {
        setNewVideoType({ name: '', factor: '1.0', description: '', icon: 'video' });
        fetchPricingConfig(true);
      }
    } catch (error) {
      console.error('Error adding video type:', error);
    }
  };

  // Delete video type
  const handleDeleteVideoType = async (videoTypeId) => {
    if (!window.confirm('Videotyp wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/pricing/config/video-type/${videoTypeId}`, { method: 'DELETE' });
      fetchPricingConfig(true);
    } catch (error) {
      console.error('Error deleting video type:', error);
    }
  };

  // Update video type factor
  const handleUpdateVideoTypeFactor = async (videoType, newFactor) => {
    try {
      const updatedVideoTypes = pricingConfig.video_types.map(vt => 
        vt.id === videoType.id ? { ...vt, factor: parseFloat(newFactor) } : vt
      );
      await fetch(`${API_URL}/api/pricing/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pricingConfig, video_types: updatedVideoTypes })
      });
      fetchPricingConfig(true);
    } catch (error) {
      console.error('Error updating video type:', error);
    }
  };

  // Add addon
  const handleAddAddon = async () => {
    if (!newAddon.name) return;
    try {
      const response = await fetch(`${API_URL}/api/pricing/config/addon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newAddon)
      });
      if (response.ok) {
        setNewAddon({ name: '', percentage: 10, description: '' });
        fetchPricingConfig(true);
      }
    } catch (error) {
      console.error('Error adding addon:', error);
    }
  };

  // Delete addon
  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Add-on wirklich löschen?')) return;
    try {
      await fetch(`${API_URL}/api/pricing/config/addon/${addonId}`, { method: 'DELETE' });
      fetchPricingConfig(true);
    } catch (error) {
      console.error('Error deleting addon:', error);
    }
  };

  // Update addon percentage
  const handleUpdateAddonPercentage = async (addon, newPercentage) => {
    try {
      const updatedAddons = pricingConfig.addons.map(a => 
        a.id === addon.id ? { ...a, percentage: parseFloat(newPercentage) } : a
      );
      await fetch(`${API_URL}/api/pricing/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...pricingConfig, addons: updatedAddons })
      });
      fetchPricingConfig(true);
    } catch (error) {
      console.error('Error updating addon:', error);
    }
  };

  // Update tier
  const handleUpdateTier = (tierType, tiers) => {
    const update = tierType === 'raw_material' 
      ? { default_raw_material_tiers: tiers }
      : { default_output_length_tiers: tiers };
    setPricingConfig(prev => ({ ...prev, ...update }));
    clearTimeout(pricingDebounceRef.current);
    pricingDebounceRef.current = setTimeout(async () => {
      try {
        await fetch(`${API_URL}/api/pricing/config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...pricingConfig, ...update })
        });
      } catch (error) {
        console.error('Error updating tier:', error);
      }
    }, 600);
  };

  // Team CRUD
  const handleSaveTeam = async () => {
    const url = editingTeam 
      ? `${API_URL}/api/teams/${editingTeam}` 
      : `${API_URL}/api/teams`;
    const method = editingTeam ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teamForm)
      });
      
      if (response.ok) {
        setShowTeamForm(false);
        setEditingTeam(null);
        resetTeamForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error saving team:', error);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!window.confirm('Team wirklich löschen?')) return;
    
    try {
      await fetch(`${API_URL}/api/teams/${teamId}`, { method: 'DELETE' });
      fetchData();
    } catch (error) {
      console.error('Error deleting team:', error);
    }
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team.id);
    setTeamForm({
      name: team.name || '',
      type: team.type || 'freelancer',
      description: team.description || '',
      location: team.location || { city: '', lat: null, lng: null },
      avatar: team.avatar || '',
      is_boosted: team.is_boosted || false,
      email: team.email || '',
      phone: team.phone || '',
      specializations: team.specializations || [],
      software_skills: team.software_skills || [],
      portfolio_links: team.portfolio_links || [],
      social_links: team.social_links || {
        instagram: '',
        youtube: '',
        twitter: '',
        website: ''
      },
      prices: team.prices || teamForm.prices,
    });
    setShowTeamForm(true);
  };

  const resetTeamForm = () => {
    setTeamForm({
      name: '',
      type: 'freelancer',
      description: '',
      location: { city: '', lat: null, lng: null },
      avatar: '',
      is_boosted: false,
      email: '',
      phone: '',
      specializations: [],
      software_skills: [],
      portfolio_links: [],
      social_links: {
        instagram: '',
        youtube: '',
        twitter: '',
        website: ''
      },
      prices: {
        basePerMinute: 80,
        motionDesign: 100,
        colorGrading: 30,
        musicLicensing: 50,
        soundDesign: 40,
        subtitles: 20,
        thumbnails: 25,
      }
    });
    setNewSpecialization('');
    setNewSoftwareSkill('');
    setNewPortfolioLink({ title: '', url: '' });
  };

  // Request status update
  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      await fetch(`${API_URL}/api/requests/${requestId}/status?status=${status}`, {
        method: 'PUT'
      });
      fetchData();
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  // Tinder-style accept/reject for requests
  const handleReviewRequest = async (status) => {
    const newRequests = requests.filter(r => r.status === 'new');
    const currentRequest = newRequests[currentRequestIndex];
    if (!currentRequest) return;

    if (status === 'accepted') {
      // Open assign modal
      setSelectedRequest(currentRequest);
      setShowAssignModal(true);
    } else {
      // Reject - move to review folder
      await handleUpdateRequestStatus(currentRequest.id, 'reviewed');
      if (currentRequestIndex >= newRequests.length - 1) {
        setCurrentRequestIndex(Math.max(0, currentRequestIndex - 1));
      }
    }
    setSwipeOffset(0);
    setReviewCardExpanded(false);
  };

  // Swipe handlers for review card
  const handleSwipeStart = (clientX) => {
    setSwipeStartX(clientX);
    setIsSwiping(true);
  };
  const handleSwipeMove = (clientX) => {
    if (swipeStartX === null || !isSwiping) return;
    setSwipeOffset(clientX - swipeStartX);
  };
  const handleSwipeEnd = () => {
    if (!isSwiping) return;
    setIsSwiping(false);
    setSwipeStartX(null);
    const threshold = 120;
    if (swipeOffset > threshold) {
      handleReviewRequest('accepted');
    } else if (swipeOffset < -threshold) {
      handleReviewRequest('rejected');
    } else {
      setSwipeOffset(0);
    }
  };

  // Toggle expanded request in "Alle Anfragen"
  const toggleRequestExpand = (id) => {
    setExpandedRequests(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Render video details for a request
  const renderVideoDetails = (request) => {
    const vids = request.videos;
    if (!vids || vids.length === 0) {
      return (
        <div className="text-sm text-gray-500 py-2">
          {request.video_count || 1}x Video, {request.minutes_per_video || 1} Min/Video
          {request.addons && Object.keys(request.addons).filter(k => request.addons[k]).length > 0 && (
            <span className="ml-2">| Addons: {Object.keys(request.addons).filter(k => request.addons[k]).join(', ')}</span>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-3">
        {vids.map((video, idx) => (
          <div key={idx} className="rounded-lg border border-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-white/5">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#5C63FF' }}>
                  {video.index || idx + 1}
                </div>
                <span className="text-sm font-medium">
                  {video.videoCount || 1}x Video • {video.minutesPerVideo || 1} Min
                  {video.format && <span className="text-gray-400 ml-1">• {video.format}</span>}
                </span>
              </div>
              {video.subtotal != null && (
                <span className="font-bold text-sm" style={{ color: '#5C63FF' }}>€{Number(video.subtotal).toFixed(0)}</span>
              )}
            </div>
            <div className="px-3 py-2 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                {video.videoType && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Typ:</span>
                    <span>{video.videoType}</span>
                  </div>
                )}
                {video.language && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Sprache:</span>
                    <span>{video.language}</span>
                  </div>
                )}
                {video.subtitleType && video.subtitleType !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Untertitel:</span>
                    <span>{video.subtitleType}{video.subtitleColor ? ` (${video.subtitleColor})` : ''}</span>
                  </div>
                )}
                {video.rawMaterialMinutes != null && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Rohmaterial:</span>
                    <span>{video.rawMaterialMinutes} Min</span>
                  </div>
                )}
                {video.needsBRoll && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">B-Roll:</span>
                    <span className="text-green-400">Ja</span>
                  </div>
                )}
                {video.rushDelivery && video.rushDelivery !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Eilauftrag:</span>
                    <span className="text-orange-400">{video.rushDelivery}</span>
                  </div>
                )}
                {video.extraRevisions && video.extraRevisions !== 'none' && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Revisionen:</span>
                    <span>{video.extraRevisions}</span>
                  </div>
                )}
              </div>
              {video.addons && Object.keys(video.addons).filter(k => video.addons[k]).length > 0 && (
                <div className="pt-1.5 mt-1.5 border-t border-white/5">
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(video.addons).filter(k => video.addons[k]).map((addon, i) => (
                      <span key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(92,99,255,0.1)', color: '#5C63FF' }}>{addon}</span>
                    ))}
                  </div>
                </div>
              )}
              {video.notes && (
                <div className="pt-1.5 mt-1.5 border-t border-white/5">
                  <p className="text-xs text-gray-400 italic">"{video.notes}"</p>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Price breakdown */}
        {(request.netto_price || request.margin_amount || request.calculated_price) && (
          <div className="border-t border-white/10 pt-3 space-y-1 text-sm">
            {request.netto_price != null && (
              <div className="flex justify-between text-gray-400">
                <span>Netto ({request.total_video_count || '-'} Videos)</span>
                <span>€{Number(request.netto_price).toFixed(0)}</span>
              </div>
            )}
            {request.margin_amount != null && (
              <div className="flex justify-between text-gray-400">
                <span>RevoCut Marge ({request.margin_percent ? `${request.margin_percent}%` : '-'})</span>
                <span>+€{Number(request.margin_amount).toFixed(0)}</span>
              </div>
            )}
            {request.calculated_price != null && (
              <div className="flex justify-between font-bold pt-1 border-t border-white/10">
                <span>Gesamtpreis</span>
                <span style={{ color: '#5C63FF' }}>€{Number(request.calculated_price).toFixed(2)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Assign job to freelancer
  const handleAssignJob = async () => {
    if (!selectedRequest || !selectedFreelancer) return;
    
    try {
      const response = await fetch(`${API_URL}/api/jobs/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: selectedRequest.id,
          freelancer_id: selectedFreelancer,
          notes: assignNotes
        })
      });
      
      if (response.ok) {
        setShowAssignModal(false);
        setSelectedRequest(null);
        setSelectedFreelancer('');
        setAssignNotes('');
        fetchData();
      }
    } catch (error) {
      console.error('Error assigning job:', error);
    }
  };

  // Helper function to safely parse JSON from fetch response (handles rrweb body consumption)
  const safeJsonParse = async (response) => {
    // Clone immediately before any read
    const responseClone = response.clone();
    
    // Try reading the text first, then parse
    try {
      const text = await response.text();
      return JSON.parse(text);
    } catch (parseError) {
      console.warn('Primary response text failed, trying clone:', parseError.message);
      try {
        const cloneText = await responseClone.text();
        return JSON.parse(cloneText);
      } catch (cloneError) {
        console.error('Both response bodies failed:', cloneError);
        // If we can't parse, return a generic error
        return { detail: 'Unbekannter Fehler beim Parsen der Antwort' };
      }
    }
  };

  // Create team profile only (for existing users without profile)
  const handleCreateTeamProfile = async () => {
    if (!selectedApplication) return;
    
    setCreateAccountLoading(true);
    setCreateAccountError('');
    
    try {
      const response = await fetch(`${API_URL}/api/freelancer/ensure-team-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selectedApplication.name,
          email: selectedApplication.email,
          specialization: selectedApplication.specialization,
          application_id: selectedApplication.id
        })
      });
      
      // Check status before trying to parse (handles rrweb interference)
      const isSuccess = response.status >= 200 && response.status < 300;
      
      let data = {};
      try {
        data = await safeJsonParse(response);
      } catch (parseError) {
        console.warn('Could not parse response:', parseError);
      }
      
      if (isSuccess) {
        // SUCCESS - Close modal and refresh
        toast.success(data.message || 'Team-Profil erfolgreich erstellt!');
        setShowCreateFreelancer(false);
        setSelectedApplication(null);
        setFreelancerPassword('');
        setCreateAccountError('');
        fetchData();
      } else {
        setCreateAccountError(data.detail || 'Team-Profil konnte nicht erstellt werden');
      }
    } catch (error) {
      console.error('Error creating team profile:', error);
      setCreateAccountError('Fehler beim Erstellen des Team-Profils');
    } finally {
      setCreateAccountLoading(false);
    }
  };

  // Approve freelancer application and create account
  const handleApproveApplication = async () => {
    if (!selectedApplication || !freelancerPassword) return;
    
    setCreateAccountLoading(true);
    setCreateAccountError('');
    
    try {
      // STEP 1: Pre-check if email already exists
      const checkResponse = await fetch(`${API_URL}/api/freelancer/check-email?email=${encodeURIComponent(selectedApplication.email)}`);
      const checkData = await checkResponse.json();
      
      if (checkData.exists) {
        // Email already registered - show error and offer "Nur Profil erstellen"
        if (checkData.has_team_profile) {
          setCreateAccountError('Diese E-Mail-Adresse ist bereits registriert UND hat bereits ein Profil. Die Bewerbung kann als "Genehmigt" markiert werden.');
        } else {
          setCreateAccountError('Diese E-Mail-Adresse ist bereits registriert. Klicken Sie auf "Nur Profil erstellen" um das Team-Profil für den existierenden Account zu erstellen.');
        }
        setCreateAccountLoading(false);
        return;
      }
      
      // STEP 2: Create freelancer user (email doesn't exist)
      const response = await fetch(`${API_URL}/api/freelancer/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: selectedApplication.name,
          email: selectedApplication.email,
          password: freelancerPassword,
          phone: selectedApplication.phone || '',
          specialization: selectedApplication.specialization
        })
      });
      
      // Store status before parsing
      const status = response.status;
      const isSuccess = status >= 200 && status < 300;
      
      if (isSuccess) {
        // STEP 3: Try to update application status (non-blocking)
        try {
          await fetch(`${API_URL}/api/freelancer-applications/${selectedApplication.id}/status?status=approved`, {
            method: 'PUT'
          });
        } catch (statusError) {
          // Status update failed (possibly CORS), but account was created successfully
          console.warn('Could not update application status:', statusError);
        }
        
        // SUCCESS - Close modal and refresh data
        toast.success(`Freelancer "${selectedApplication.name}" wurde erfolgreich erstellt!`);
        setShowCreateFreelancer(false);
        setSelectedApplication(null);
        setFreelancerPassword('');
        setCreateAccountError('');
        fetchData();
      } else {
        // Unexpected error
        setCreateAccountError('Account konnte nicht erstellt werden. Bitte versuchen Sie es erneut.');
      }
    } catch (error) {
      console.error('Error creating freelancer:', error);
      setCreateAccountError('Fehler beim Erstellen des Accounts. Bitte versuchen Sie es erneut.');
    } finally {
      setCreateAccountLoading(false);
    }
  };

  // Update application status
  const handleUpdateApplicationStatus = async (appId, status) => {
    try {
      await fetch(`${API_URL}/api/freelancer-applications/${appId}/status?status=${status}`, {
        method: 'PUT'
      });
      fetchData();
    } catch (error) {
      console.error('Error updating application:', error);
    }
  };

  // Create payment link
  const handleCreatePaymentLink = async () => {
    if (!selectedJobForPayment) return;
    setPaymentLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/create-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_id: selectedJobForPayment.id,
          amount: selectedJobForPayment.project_details?.calculated_price || 0,
          description: `Video-Projekt: ${selectedJobForPayment.project_details?.video_count || 1} Videos`,
          customer_email: selectedJobForPayment.customer_email,
          customer_name: selectedJobForPayment.customer_name
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedPaymentLink(data.payment_link);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      alert('Fehler beim Erstellen des Zahlungslinks');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Send invoice email
  const handleSendInvoice = async () => {
    if (!selectedJobForPayment || !generatedPaymentLink) return;
    setPaymentLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/send-invoice?job_id=${selectedJobForPayment.id}&payment_link=${encodeURIComponent(generatedPaymentLink)}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        alert('Rechnung erfolgreich gesendet!');
        setShowPaymentModal(false);
        setSelectedJobForPayment(null);
        setGeneratedPaymentLink(null);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Fehler: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      alert('Fehler beim Senden der Rechnung');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('revocut_admin');
    setIsAuthenticated(false);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-9 h-9" />
            <span className="text-2xl font-bold text-white ml-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              REVOCUT
            </span>
          </div>
        </div>

        <div className="w-full max-w-md">
          <div 
            className="p-8 rounded-2xl border border-white/10"
            style={{ backgroundColor: '#1a1a1a' }}
          >
            <h1 className="text-2xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Admin Login
            </h1>
            <p className="text-gray-400 text-center mb-6">
              Zugang zum Dashboard
            </p>

            {/* Login Mode Toggle */}
            <div className="flex mb-6 p-1 rounded-xl bg-white/5">
              <button
                type="button"
                onClick={() => setLoginMode('email')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  loginMode === 'email' ? 'text-white' : 'text-gray-400'
                }`}
                style={{ backgroundColor: loginMode === 'email' ? '#5C63FF' : 'transparent' }}
              >
                Mit E-Mail
              </button>
              <button
                type="button"
                onClick={() => setLoginMode('password')}
                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  loginMode === 'password' ? 'text-white' : 'text-gray-400'
                }`}
                style={{ backgroundColor: loginMode === 'password' ? '#5C63FF' : 'transparent' }}
              >
                Nur Passwort
              </button>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {loginError}
                </div>
              )}
              
              {loginMode === 'email' && (
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="E-Mail Adresse"
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                    required
                  />
                </div>
              )}
              
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02]"
                style={{ backgroundColor: '#5C63FF' }}
              >
                Einloggen
              </button>
            </form>
            
            {loginMode === 'email' && (
              <button
                type="button"
                onClick={() => navigate('/admin/reset-password')}
                className="w-full mt-4 text-sm text-gray-400 hover:text-purple-400 transition-colors"
              >
                Passwort vergessen?
              </button>
            )}
          </div>
          
          <button 
            onClick={() => navigate('/')}
            className="w-full mt-6 text-sm text-gray-500 hover:text-white transition-colors flex items-center justify-center gap-2"
          >
            ← Zurück zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <header className="px-6 py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-10 h-10" />
            <div>
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                REVOCUT
              </span>
              <span className="ml-2 px-2 py-0.5 rounded-md text-xs font-medium" style={{ backgroundColor: '#5C63FF' }}>
                Admin
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative" ref={notifBellRef}>
              <button
                onClick={() => { setShowNotifDropdown(!showNotifDropdown); }}
                className="relative p-2.5 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all"
                data-testid="notification-bell"
              >
                {unreadCount > 0 ? (
                  <BellRing className="w-5 h-5 animate-[wiggle_0.5s_ease-in-out]" style={{ color: '#5C63FF' }} />
                ) : (
                  <Bell className="w-5 h-5" />
                )}
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 animate-pulse" data-testid="notification-badge">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifDropdown && (
                <div className="absolute right-0 top-12 w-80 rounded-xl border border-white/10 shadow-2xl z-50 overflow-hidden" style={{ backgroundColor: '#1a1a1a' }} data-testid="notification-dropdown">
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <span className="font-bold text-sm">Benachrichtigungen</span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={toggleSound}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-all"
                        title={soundEnabled ? 'Sound aus' : 'Sound an'}
                        data-testid="notification-sound-toggle"
                      >
                        {soundEnabled ? <Volume2 className="w-4 h-4 text-green-400" /> : <VolumeX className="w-4 h-4 text-gray-500" />}
                      </button>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllRead}
                          className="text-xs px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
                          style={{ color: '#5C63FF' }}
                          data-testid="notification-mark-read"
                        >
                          Alle gelesen
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-72 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-gray-500 text-sm">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        Keine Benachrichtigungen
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`px-4 py-3 border-b border-white/5 transition-all hover:bg-white/5 ${!notif.read ? 'bg-white/[0.03]' : ''}`}
                          data-testid={`notification-item-${notif.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              notif.type === 'request' ? 'bg-yellow-500/15' : 'bg-purple-500/15'
                            }`}>
                              {notif.type === 'request' ? (
                                <FileText className="w-4 h-4 text-yellow-400" />
                              ) : (
                                <UserPlus className="w-4 h-4 text-purple-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-medium text-white' : 'text-gray-400'}`}>
                                {notif.text}
                              </p>
                              <p className="text-xs text-gray-600 mt-0.5">
                                {notif.time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: '#5C63FF' }} />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={handleLogout}
              className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/10 transition-all"
            >
              Ausloggen
            </button>
          </div>
        </div>
      </header>

      {/* Stats */}
      {stats && (
        <div className="px-6 py-6 border-b border-white/10">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}>
                  <Users className="w-6 h-6" style={{ color: '#5C63FF' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.teams_count}</p>
                  <p className="text-sm text-gray-500">Freelancer</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)' }}>
                  <FileText className="w-6 h-6" style={{ color: '#5C63FF' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.requests_count}</p>
                  <p className="text-sm text-gray-500">Anfragen</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-yellow-500/15">
                  <Mail className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.new_requests}</p>
                  <p className="text-sm text-gray-500">Neue</p>
                </div>
              </div>
            </div>
            <div className="p-5 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-green-500/15">
                  <BarChart3 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">€{stats.total_estimated_value?.toFixed(0) || 0}</p>
                  <p className="text-sm text-gray-500">Geschätzt</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex gap-2 overflow-x-auto pb-1">
          <button 
            onClick={() => setActiveTab('review')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'review' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'review' ? '#5C63FF' : 'transparent' }}
          >
            <ThumbsUp className="w-4 h-4 inline mr-2" />
            Anfragen prüfen
            {requests.filter(r => r.status === 'new').length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-yellow-500 text-black text-xs rounded-full font-bold">
                {requests.filter(r => r.status === 'new').length}
              </span>
            )}
          </button>
          <button 
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'requests' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'requests' ? '#5C63FF' : 'transparent' }}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Alle Anfragen
          </button>
          <button 
            onClick={() => setActiveTab('jobs')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'jobs' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'jobs' ? '#5C63FF' : 'transparent' }}
          >
            <Briefcase className="w-4 h-4 inline mr-2" />
            Jobs
            {jobs.filter(j => j.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-bold">
                {jobs.filter(j => j.status === 'pending').length}
              </span>
            )}
          </button>
          <button 
            onClick={() => setActiveTab('applications')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'applications' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'applications' ? '#5C63FF' : 'transparent' }}
          >
            <UserCheck className="w-4 h-4 inline mr-2" />
            Bewerbungen
            {freelancerApplications.filter(a => a.status === 'pending').length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-green-500 text-black text-xs rounded-full font-bold">
                {freelancerApplications.filter(a => a.status === 'pending').length}
              </span>
            )}
          </button>
          <button 
            onClick={() => setActiveTab('teams')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'teams' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'teams' ? '#5C63FF' : 'transparent' }}
          >
            <MapPin className="w-4 h-4 inline mr-2" />
            Freelancer
          </button>
          <button 
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'analytics' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'analytics' ? '#5C63FF' : 'transparent' }}
          >
            <TrendingUp className="w-4 h-4 inline mr-2" />
            Analytics
          </button>
          <button 
            onClick={() => { setActiveTab('pricing'); fetchPricingConfig(); fetchMarginSettings(); }}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'pricing' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'pricing' ? '#5C63FF' : 'transparent' }}
          >
            <DollarSign className="w-4 h-4 inline mr-2" />
            Preise
          </button>
          <button
            onClick={() => setActiveTab('chatbot')}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'chatbot' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'chatbot' ? '#5C63FF' : 'transparent' }}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Chatbot
          </button>
          <button
            onClick={() => { setActiveTab('invoices'); fetchAllInvoices(); }}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'invoices' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'invoices' ? '#5C63FF' : 'transparent' }}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Rechnungen
          </button>
          <button
            onClick={() => { setActiveTab('tags'); fetchTagCategories(); }}
            className={`px-4 py-2.5 rounded-xl transition-all whitespace-nowrap font-medium text-sm ${
              activeTab === 'tags' ? 'text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
            style={{ backgroundColor: activeTab === 'tags' ? '#5C63FF' : 'transparent' }}
            data-testid="tab-tags"
          >
            <Tag className="w-4 h-4 inline mr-2" />
            Tags
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="px-6 py-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
              <p className="text-gray-400">Wird geladen...</p>
            </div>
          ) : activeTab === 'review' ? (
            <>
              {/* Tinder-Style Request Review */}
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Neue Anfragen prüfen
              </h2>

              {(() => {
                const newRequests = requests.filter(r => r.status === 'new');
                const currentRequest = newRequests[currentRequestIndex];
                
                if (newRequests.length === 0) {
                  return (
                    <div className="p-12 rounded-2xl border border-white/10 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                      <Check className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p className="text-gray-400">Alle Anfragen wurden geprüft!</p>
                    </div>
                  );
                }

                return (
                  <div className="max-w-2xl mx-auto">
                    {/* Progress */}
                    <div className="text-center text-gray-400 text-sm mb-4">
                      {currentRequestIndex + 1} von {newRequests.length}
                    </div>

                    {/* Swipe hint */}
                    <div className="flex justify-between items-center mb-3 px-4">
                      <span className={`text-sm font-medium transition-opacity ${swipeOffset < -40 ? 'opacity-100 text-red-400' : 'opacity-30 text-gray-500'}`}>
                        <ThumbsDown className="w-4 h-4 inline mr-1" /> Ablehnen
                      </span>
                      <span className="text-xs text-gray-600">Karte swipen oder Buttons nutzen</span>
                      <span className={`text-sm font-medium transition-opacity ${swipeOffset > 40 ? 'opacity-100 text-green-400' : 'opacity-30 text-gray-500'}`}>
                        Annehmen <ThumbsUp className="w-4 h-4 inline ml-1" />
                      </span>
                    </div>

                    {/* Swipeable Request Card */}
                    <div
                      data-testid="review-swipe-card"
                      className="rounded-2xl border border-white/10 p-8 relative select-none cursor-grab active:cursor-grabbing transition-shadow"
                      style={{ 
                        backgroundColor: '#1a1a1a',
                        transform: `translateX(${swipeOffset}px) rotate(${swipeOffset * 0.04}deg)`,
                        transition: isSwiping ? 'none' : 'transform 0.3s ease',
                        boxShadow: swipeOffset > 40 ? '0 0 30px rgba(16,185,129,0.15)' : swipeOffset < -40 ? '0 0 30px rgba(239,68,68,0.15)' : 'none',
                        borderColor: swipeOffset > 40 ? 'rgba(16,185,129,0.4)' : swipeOffset < -40 ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.1)',
                      }}
                      onMouseDown={(e) => { e.preventDefault(); handleSwipeStart(e.clientX); }}
                      onMouseMove={(e) => handleSwipeMove(e.clientX)}
                      onMouseUp={handleSwipeEnd}
                      onMouseLeave={() => { if (isSwiping) handleSwipeEnd(); }}
                      onTouchStart={(e) => handleSwipeStart(e.touches[0].clientX)}
                      onTouchMove={(e) => handleSwipeMove(e.touches[0].clientX)}
                      onTouchEnd={handleSwipeEnd}
                    >
                      {/* Swipe Overlay Indicators */}
                      {swipeOffset > 40 && (
                        <div className="absolute top-6 left-6 px-4 py-2 rounded-xl bg-green-500/20 border-2 border-green-500 text-green-400 font-bold text-lg -rotate-12 z-10">
                          ANNEHMEN
                        </div>
                      )}
                      {swipeOffset < -40 && (
                        <div className="absolute top-6 right-6 px-4 py-2 rounded-xl bg-red-500/20 border-2 border-red-500 text-red-400 font-bold text-lg rotate-12 z-10">
                          ABLEHNEN
                        </div>
                      )}

                      {/* Price Badge */}
                      <div className="absolute top-6 right-6 px-4 py-2 rounded-full font-bold" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)', color: '#5C63FF' }}>
                        €{currentRequest.calculated_price?.toFixed(2) || '0'}
                      </div>

                      <h3 className="text-2xl font-bold mb-1">{currentRequest.name}</h3>
                      <p className="text-gray-400 mb-2">{currentRequest.email}</p>
                      
                      {/* Quick info */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {currentRequest.company && (
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-sm text-gray-300 flex items-center gap-1.5">
                            <Briefcase className="w-3.5 h-3.5 text-gray-500" /> {currentRequest.company}
                          </span>
                        )}
                        {currentRequest.team_name && (
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-sm text-gray-300 flex items-center gap-1.5">
                            <Users className="w-3.5 h-3.5 text-gray-500" /> {currentRequest.team_name}
                          </span>
                        )}
                        {currentRequest.phone && (
                          <span className="px-2.5 py-1 rounded-lg bg-white/5 text-sm text-gray-300 flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-gray-500" /> {currentRequest.phone}
                          </span>
                        )}
                        <span className="px-2.5 py-1 rounded-lg bg-white/5 text-sm text-gray-300 flex items-center gap-1.5">
                          <Video className="w-3.5 h-3.5 text-gray-500" /> 
                          {currentRequest.total_video_count || currentRequest.videos?.reduce((s,v) => s + (v.videoCount || 1), 0) || currentRequest.video_count || 1} Videos
                        </span>
                      </div>

                      {/* Message */}
                      {currentRequest.message && (
                        <div className="p-3 rounded-lg bg-white/5 mb-4 text-sm text-gray-300">
                          <Mail className="w-4 h-4 inline mr-2 text-gray-500" />
                          {currentRequest.message}
                        </div>
                      )}

                      {/* Expandable Video Details */}
                      <button
                        onClick={() => setReviewCardExpanded(!reviewCardExpanded)}
                        className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg bg-white/5 hover:bg-white/8 transition-all text-sm text-gray-300 mb-4"
                        data-testid="review-expand-details"
                      >
                        <span className="flex items-center gap-2">
                          <Layers className="w-4 h-4" style={{ color: '#5C63FF' }} />
                          Projektdetails anzeigen
                          {currentRequest.videos?.length > 0 && (
                            <span className="text-xs text-gray-500">({currentRequest.videos.length} Video{currentRequest.videos.length > 1 ? 's' : ''})</span>
                          )}
                        </span>
                        {reviewCardExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>

                      {reviewCardExpanded && (
                        <div className="mb-6 max-h-80 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                          {renderVideoDetails(currentRequest)}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex justify-center gap-6">
                        <button
                          onClick={() => handleReviewRequest('rejected')}
                          className="w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                          style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
                          data-testid="review-reject-btn"
                        >
                          <ThumbsDown className="w-8 h-8 text-red-500" />
                        </button>
                        
                        <button
                          onClick={() => handleReviewRequest('accepted')}
                          className="w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-110"
                          style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}
                          data-testid="review-accept-btn"
                        >
                          <ThumbsUp className="w-8 h-8 text-green-500" />
                        </button>
                      </div>

                      <p className="text-center text-gray-500 text-sm mt-4">
                        Swipen oder Buttons nutzen
                      </p>
                    </div>

                    {/* Navigation */}
                    {newRequests.length > 1 && (
                      <div className="flex justify-center gap-2 mt-4">
                        {newRequests.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => { setCurrentRequestIndex(idx); setReviewCardExpanded(false); setSwipeOffset(0); }}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentRequestIndex ? 'scale-125' : 'opacity-40'}`}
                            style={{ backgroundColor: idx === currentRequestIndex ? '#5C63FF' : '#666' }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          ) : activeTab === 'jobs' ? (
            <>
              {/* Jobs Overview */}
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Jobs Übersicht
              </h2>

              {jobs.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Noch keine Jobs zugewiesen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobs.map((job) => (
                    <div key={job.id} className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div>
                          <h3 className="font-bold text-lg">{job.customer_name}</h3>
                          <p className="text-sm text-gray-400">Freelancer: {job.freelancer_name}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            job.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            job.status === 'accepted' ? 'bg-orange-500/20 text-orange-400' :
                            job.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            job.status === 'in_progress' ? 'bg-purple-500/20 text-purple-400' :
                            job.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            job.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {job.status === 'pending' ? 'Ausstehend' :
                             job.status === 'accepted' ? 'Angenommen - Freigabe nötig' :
                             job.status === 'approved' ? 'Freigegeben' :
                             job.status === 'in_progress' ? 'In Bearbeitung' :
                             job.status === 'completed' ? 'Abgeschlossen' :
                             job.status === 'rejected' ? 'Abgelehnt' :
                             job.status}
                          </span>
                          <span className="text-lg font-bold" style={{ color: '#5C63FF' }}>
                            €{job.project_details?.calculated_price?.toFixed(2) || '0'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Kunde E-Mail</p>
                          <p>{job.customer_email}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Videos</p>
                          <p>{job.project_details?.video_count || 1} × {job.project_details?.minutes_per_video || 1} Min</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Erstellt</p>
                          <p>{new Date(job.created_at).toLocaleDateString('de-DE')}</p>
                        </div>
                        {job.admin_notes && (
                          <div>
                            <p className="text-gray-500">Admin Notiz</p>
                            <p>{job.admin_notes}</p>
                          </div>
                        )}
                      </div>

                      {/* Freelancer Notes */}
                      {job.freelancer_response_notes && (
                        <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20 mb-4">
                          <p className="text-xs text-yellow-400 font-medium mb-1">Notiz vom Freelancer:</p>
                          <p className="text-sm text-gray-300">{job.freelancer_response_notes}</p>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-2 flex-wrap">
                        {/* Approve Button for accepted jobs */}
                        {job.status === 'accepted' && (
                          <button 
                            data-testid={`approve-job-${job.id}`}
                            onClick={async () => {
                              try {
                                const res = await fetch(`${API_URL}/api/jobs/${job.id}/approve`, { method: 'PUT' });
                                if (res.ok) {
                                  toast.success('Auftrag freigegeben!');
                                  const jobsRefresh = await fetch(`${API_URL}/api/jobs/all`);
                                  if (jobsRefresh.ok) setJobs(await jobsRefresh.json());
                                } else {
                                  toast.error('Fehler beim Freigeben');
                                }
                              } catch (e) {
                                toast.error('Netzwerkfehler');
                              }
                            }}
                            className="px-4 py-2 rounded-lg text-sm bg-green-500/20 text-green-400 hover:bg-green-500/30 flex items-center gap-2 font-medium border border-green-500/30"
                          >
                            <CheckCircle className="w-4 h-4" /> Auftrag freigeben
                          </button>
                        )}
                        {job.payment_link ? (
                          <a 
                            href={job.payment_link}
                            target="_blank"
                            rel="noreferrer"
                            className="px-3 py-1.5 rounded-lg text-sm bg-green-500/10 text-green-400 hover:bg-green-500/20 flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" /> Zahlungslink öffnen
                          </a>
                        ) : (
                          <button 
                            onClick={() => { setSelectedJobForPayment(job); setShowPaymentModal(true); setGeneratedPaymentLink(null); }}
                            className="px-3 py-1.5 rounded-lg text-sm bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 flex items-center gap-1"
                          >
                            <CreditCard className="w-3 h-3" /> Rechnung erstellen
                          </button>
                        )}
                        <a 
                          href={`mailto:${job.customer_email}`}
                          className="px-3 py-1.5 rounded-lg text-sm bg-white/5 text-gray-300 hover:bg-white/10 flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" /> E-Mail senden
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'applications' ? (
            <>
              {/* Freelancer Applications */}
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Freelancer Bewerbungen
              </h2>

              {freelancerApplications.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <UserCheck className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Noch keine Bewerbungen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {freelancerApplications.map((app) => (
                    <div key={app.id} className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                        <div>
                          <h3 className="font-bold text-lg">{app.name}</h3>
                          <p className="text-sm text-gray-400">{app.email}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          app.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                          app.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {app.status === 'pending' ? 'Ausstehend' :
                           app.status === 'approved' ? 'Genehmigt' :
                           app.status === 'rejected' ? 'Abgelehnt' :
                           app.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                        <div>
                          <p className="text-gray-500">Telefon</p>
                          <p>{app.phone || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Spezialisierung</p>
                          <p>{app.specialization || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Firma</p>
                          <p>{app.company || '-'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Website</p>
                          {app.website ? (
                            <a href={app.website} target="_blank" rel="noreferrer" className="text-purple-400 hover:underline">Öffnen</a>
                          ) : <p>-</p>}
                        </div>
                      </div>

                      {app.portfolio && (
                        <div className="mb-4">
                          <p className="text-gray-500 text-sm mb-1">Portfolio</p>
                          <a href={app.portfolio} target="_blank" rel="noreferrer" className="text-purple-400 hover:underline text-sm">{app.portfolio}</a>
                        </div>
                      )}

                      {app.message && (
                        <div className="p-3 rounded-lg bg-white/5 mb-4">
                          <p className="text-sm text-gray-400">{app.message}</p>
                        </div>
                      )}

                      {app.status === 'pending' && (
                        <div className="flex gap-2">
                          <button 
                            onClick={() => { setSelectedApplication(app); setShowCreateFreelancer(true); }}
                            className="px-4 py-2 rounded-lg text-sm bg-green-500/10 text-green-400 hover:bg-green-500/20 flex items-center gap-2"
                          >
                            <Check className="w-4 h-4" /> Genehmigen & Account erstellen
                          </button>
                          <button 
                            onClick={() => handleUpdateApplicationStatus(app.id, 'rejected')}
                            className="px-4 py-2 rounded-lg text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center gap-2"
                          >
                            <X className="w-4 h-4" /> Ablehnen
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'analytics' ? (
            <>
              {/* Analytics Tab */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <TrendingUp className="w-5 h-5 inline mr-2" />
                  Analytics Dashboard
                </h2>
                <div className="flex gap-2">
                  {['week', 'month', 'year'].map((period) => (
                    <button
                      key={period}
                      onClick={() => handlePeriodChange(period)}
                      className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                        analyticsPeriod === period 
                          ? 'text-white' 
                          : 'text-gray-400 hover:text-white bg-white/5'
                      }`}
                      style={{ backgroundColor: analyticsPeriod === period ? '#5C63FF' : '' }}
                    >
                      {period === 'week' ? 'Woche' : period === 'month' ? 'Monat' : 'Jahr'}
                    </button>
                  ))}
                </div>
              </div>

              {loadingAnalytics ? (
                <div className="text-center py-20">
                  <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                  <p className="text-gray-400">Analytics werden geladen...</p>
                </div>
              ) : analytics ? (
                <div className="space-y-6">
                  {/* Overview Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-blue-400" />
                        <span className="text-sm text-gray-400">Anfragen</span>
                      </div>
                      <p className="text-2xl font-bold">{analytics.overview.total_requests}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-5 h-5 text-purple-400" />
                        <span className="text-sm text-gray-400">Jobs</span>
                      </div>
                      <p className="text-2xl font-bold">{analytics.overview.total_jobs}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-green-400" />
                        <span className="text-sm text-gray-400">Freelancer</span>
                      </div>
                      <p className="text-2xl font-bold">{analytics.overview.total_freelancers}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-yellow-400" />
                        <span className="text-sm text-gray-400">Umsatz</span>
                      </div>
                      <p className="text-2xl font-bold">€{(analytics.overview.total_revenue || 0).toFixed(0)}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="w-5 h-5 text-orange-400" />
                        <span className="text-sm text-gray-400">Ausstehend</span>
                      </div>
                      <p className="text-2xl font-bold">€{(analytics.overview.pending_revenue || 0).toFixed(0)}</p>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-4" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Activity className="w-5 h-5 text-pink-400" />
                        <span className="text-sm text-gray-400">Conversion</span>
                      </div>
                      <p className="text-2xl font-bold">{analytics.conversion.rate}%</p>
                    </div>
                  </div>

                  {/* Charts Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Jobs Status */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-purple-400" />
                        Job Status
                      </h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Ausstehend</span>
                          <div className="flex items-center gap-3">
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-yellow-500 rounded-full"
                                style={{ width: `${(analytics.jobs.pending / (analytics.overview.total_jobs || 1)) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium w-8">{analytics.jobs.pending}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">In Bearbeitung</span>
                          <div className="flex items-center gap-3">
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${(analytics.jobs.in_progress / (analytics.overview.total_jobs || 1)) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium w-8">{analytics.jobs.in_progress}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400">Abgeschlossen</span>
                          <div className="flex items-center gap-3">
                            <div className="w-32 h-2 bg-white/10 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${(analytics.jobs.completed / (analytics.overview.total_jobs || 1)) * 100}%` }}
                              />
                            </div>
                            <span className="text-white font-medium w-8">{analytics.jobs.completed}</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-sm text-gray-400">
                          Abschlussrate: <span className="text-white font-bold">{analytics.jobs.completion_rate}%</span>
                        </p>
                      </div>
                    </div>

                    {/* Top Freelancers */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        Top Freelancer
                      </h3>
                      {analytics.top_freelancers.length === 0 ? (
                        <p className="text-gray-400 text-center py-8">Noch keine Daten vorhanden</p>
                      ) : (
                        <div className="space-y-3">
                          {analytics.top_freelancers.map((f, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                              <div className="flex items-center gap-3">
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                  i === 0 ? 'bg-yellow-500 text-black' :
                                  i === 1 ? 'bg-gray-400 text-black' :
                                  i === 2 ? 'bg-orange-600 text-white' :
                                  'bg-white/10 text-white'
                                }`}>
                                  {i + 1}
                                </span>
                                <span className="font-medium">{f.name}</span>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-green-400">€{(f.earnings || 0).toFixed(0)}</p>
                                <p className="text-xs text-gray-400">{f.jobs} Jobs</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Monthly Revenue Chart */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Umsatz-Entwicklung (letzte 6 Monate)
                    </h3>
                    <div className="flex items-end justify-between gap-4" style={{ height: '200px' }}>
                      {analytics.monthly_revenue.map((m, i) => {
                        const maxRevenue = Math.max(...analytics.monthly_revenue.map(x => x.revenue || 0)) || 1;
                        const barHeight = Math.max(((m.revenue || 0) / maxRevenue) * 150, 4);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center h-full">
                            <div className="flex-1 w-full flex items-end justify-center">
                              <div 
                                className="rounded-t-lg transition-all duration-500"
                                style={{ 
                                  height: `${barHeight}px`,
                                  width: '100%',
                                  maxWidth: '48px',
                                  backgroundColor: '#5C63FF',
                                  opacity: m.revenue > 0 ? 1 : 0.3
                                }}
                              />
                            </div>
                            <span className="text-xs text-gray-400 mt-2">{m.month.split(' ')[0]}</span>
                            <span className="text-xs font-medium" style={{ color: m.revenue > 0 ? '#5C63FF' : '#6b7280' }}>€{(m.revenue || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 })}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Requests by Status */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-400" />
                      Anfragen nach Status
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {Object.entries(analytics.requests_by_status).map(([status, count]) => (
                        <div key={status} className="text-center p-4 rounded-lg bg-white/5">
                          <p className="text-2xl font-bold">{count}</p>
                          <p className="text-sm text-gray-400">
                            {status === 'new' ? 'Neu' :
                             status === 'contacted' ? 'Kontaktiert' :
                             status === 'assigned' ? 'Zugewiesen' :
                             status === 'completed' ? 'Abgeschlossen' :
                             status === 'cancelled' ? 'Storniert' : status}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Keine Analytics-Daten verfügbar</p>
                </div>
              )}
            </>
          ) : activeTab === 'pricing' ? (
            <>
              {/* Pricing Configuration Tab - Baukasten System */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    Preiskonfiguration (Baukasten)
                  </h2>
                  <p className="text-sm text-gray-400">Definiere hier die Struktur - Freelancer füllen dann ihre Werte aus</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={async () => {
                      if (window.confirm('Preiskonfiguration auf Standardwerte zurücksetzen?')) {
                        await fetch(`${API_URL}/api/pricing/config/reset`, { method: 'POST' });
                        fetchPricingConfig();
                      }
                    }}
                    className="px-4 py-2 rounded-xl bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Reset
                  </button>
                  <button
                    onClick={() => navigate('/freelancer/dashboard')}
                    className="px-4 py-2 rounded-xl flex items-center gap-2" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)', color: '#5C63FF' }}
                  >
                    <ExternalLink className="w-4 h-4" />
                    Zur RevoCut-Ansicht
                  </button>
                </div>
              </div>
              
              {loadingPricing ? (
                <div className="text-center py-20">
                  <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                  <p className="text-gray-400">Wird geladen...</p>
                </div>
              ) : pricingConfig ? (
                <div className="space-y-6">
                  
                  {/* RevoCut Marge - Gestaffelt */}
                  <div className="rounded-2xl border p-6" style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)', borderColor: 'rgba(92, 99, 255, 0.3)' }}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-bold flex items-center gap-2">
                        <TrendingUp className="w-5 h-5" style={{ color: '#5C63FF' }} />
                        RevoCut Marge (Vermittlungsgebühr)
                      </h3>
                      <button
                        onClick={saveMarginSettings}
                        className="px-4 py-2 rounded-xl flex items-center gap-2" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)', color: '#5C63FF' }}
                      >
                        <Save className="w-4 h-4" />
                        Speichern
                      </button>
                    </div>
                    <p className="text-sm text-gray-400 mb-4">
                      Diese Marge wird auf den Netto-Gesamtpreis aufgeschlagen und ist die Vermittlungsgebühr von RevoCut.
                    </p>
                    
                    {/* Margin Tiers */}
                    <div className="space-y-3">
                      {marginTiers.sort((a, b) => a.min_amount - b.min_amount).map((tier, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex-1 grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs text-gray-500">Ab Betrag (€)</label>
                              <input
                                type="number"
                                value={tier.min_amount}
                                onChange={(e) => updateMarginTier(index, 'min_amount', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Marge (%)</label>
                              <input
                                type="number"
                                step="0.5"
                                value={tier.margin_percent}
                                onChange={(e) => updateMarginTier(index, 'margin_percent', e.target.value === '' ? '' : parseFloat(e.target.value))}
                                className="w-full px-3 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)', borderColor: 'rgba(92, 99, 255, 0.3)', border: '1px solid rgba(92, 99, 255, 0.3)', color: '#5C63FF' }}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Beschreibung</label>
                              <input
                                type="text"
                                value={tier.description || ''}
                                onChange={(e) => updateMarginTier(index, 'description', e.target.value)}
                                placeholder="z.B. Bis €500"
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => removeMarginTier(index)}
                            className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add Tier Button */}
                    <button
                      onClick={addMarginTier}
                      className="mt-4 w-full py-3 rounded-xl border-2 border-dashed flex items-center justify-center gap-2" style={{ borderColor: 'rgba(92, 99, 255, 0.3)', color: '#5C63FF' }}
                    >
                      <Plus className="w-4 h-4" />
                      Staffel hinzufügen
                    </button>
                    
                    {/* Preview */}
                    <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                      <p className="text-xs text-gray-500 mb-2">Vorschau:</p>
                      <div className="flex flex-wrap gap-2">
                        {marginTiers.sort((a, b) => a.min_amount - b.min_amount).map((tier, index) => (
                          <span key={index} className="px-3 py-1 rounded-full text-xs" style={{ backgroundColor: 'rgba(92, 99, 255, 0.15)', color: '#5C63FF' }}>
                            ab €{tier.min_amount}: {tier.margin_percent}%
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  
                  {/* ===== Section 2: Video-Formate (full-width) ===== */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Video className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Video-Formate
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {pricingConfig.video_formats?.map((fmt) => (
                        <div key={fmt.id} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                          <span className="text-sm font-medium">{fmt.name}</span>
                          <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/video-format/${fmt.id}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button onClick={async () => { const name = prompt('Neues Format (z.B. "1:1 Square"):'); if (name) { try { await fetch(`${API_URL}/api/pricing/config/video-format`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }); } catch(e){} fetchPricingConfig(true); } }} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-white/20 hover:text-white text-gray-400" style={{ '--hover-border': '#5C63FF' }}><Plus className="w-4 h-4" /> Format</button>
                    </div>
                  </div>

                  {/* ===== Section 3: Videotypen (full-width) ===== */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Video className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Videotypen
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                      {pricingConfig.video_types?.map((vt) => (
                        <div key={vt.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                          <GripVertical className="w-4 h-4 text-gray-500 cursor-grab" />
                          <div className="flex-1">
                            <input type="text" value={vt.name} onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, video_types: prev.video_types.map(v => v.id === vt.id ? { ...v, name: val } : v) }), () => fetch(`${API_URL}/api/pricing/config/video-type/${vt.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...vt, name: val }) })); }} className="font-medium text-sm bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none w-full" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                            <input type="text" value={vt.description || ''} placeholder="Beschreibung..." onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, video_types: prev.video_types.map(v => v.id === vt.id ? { ...v, description: val } : v) }), () => fetch(`${API_URL}/api/pricing/config/video-type/${vt.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...vt, description: val }) })); }} className="text-xs text-gray-500 bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none w-full" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                          </div>
                          <button onClick={() => handleDeleteVideoType(vt.id)} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" placeholder="Neuer Videotyp" value={newVideoType.name} onChange={(e) => setNewVideoType({...newVideoType, name: e.target.value})} className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-sm" />
                      <button onClick={handleAddVideoType} className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#5C63FF', color: 'white' }}><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>

                  {/* ===== Section 4: Sprachen + Untertitel (side-by-side) ===== */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Languages */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><Globe className="w-5 h-5" style={{ color: '#5C63FF' }} />Sprachen</h3>
                      <div className="space-y-2 mb-4">
                        {pricingConfig.languages?.sort((a, b) => a.order - b.order).map((lang) => (
                          <div key={lang.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <input type="text" value={lang.name} onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, languages: prev.languages.map(l => l.id === lang.id ? { ...l, name: val } : l) }), () => fetch(`${API_URL}/api/pricing/config/language/${lang.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: val }) })); }} className="flex-1 text-sm font-medium bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                            <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/language/${lang.id}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <button onClick={async () => { const name = prompt('Neue Sprache:'); if (name) { try { await fetch(`${API_URL}/api/pricing/config/language`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) }); } catch(e){} fetchPricingConfig(true); } }} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white" style={{ '--hover-border': '#5C63FF' }}>+ Sprache hinzufügen</button>
                    </div>
                    {/* Subtitle Types */}
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><FileText className="w-5 h-5" style={{ color: '#5C63FF' }} />Untertitel-Typen</h3>
                      <div className="space-y-2 mb-4">
                        {pricingConfig.subtitle_types?.filter(s => s.id !== 'none').sort((a, b) => a.order - b.order).map((st) => (
                          <div key={st.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <input type="text" value={st.name} onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, subtitle_types: prev.subtitle_types.map(s => s.id === st.id ? { ...s, name: val } : s) }), () => fetch(`${API_URL}/api/pricing/config/subtitle-type/${st.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...st, name: val }) })); }} className="flex-1 text-sm font-medium bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                            <div className="flex items-center gap-1"><span className="text-xs" style={{ color: '#5C63FF' }}>€</span><input type="number" step="1" value={st.flat_price || 0} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, subtitle_types: prev.subtitle_types.map(s => s.id === st.id ? { ...s, flat_price: val } : s) }), () => fetch(`${API_URL}/api/pricing/config/subtitle-type/${st.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...st, flat_price: val }) })); }} className="w-16 px-2 py-1 rounded-lg text-center text-sm" style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)', color: '#5C63FF', border: '1px solid rgba(92, 99, 255, 0.3)' }} /></div>
                            <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/subtitle-type/${st.id}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <button onClick={async () => { const name = prompt('Neuer Untertitel-Typ:'); if (name) { const price = parseFloat(prompt('Preis (€):', '15')) || 0; try { await fetch(`${API_URL}/api/pricing/config/subtitle-type`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, flat_price: price, has_color: true }) }); } catch(e){} fetchPricingConfig(true); } }} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white">+ Untertitel-Typ hinzufügen</button>
                    </div>
                  </div>

                  {/* ===== Section 5: Untertitel-Farben (full-width) ===== */}
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2"><Palette className="w-5 h-5" style={{ color: '#5C63FF' }} />Untertitel-Farben</h3>
                    <div className="flex flex-wrap gap-3">
                      {pricingConfig.subtitle_colors?.sort((a, b) => a.order - b.order).map((color) => (
                        <div key={color.id} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10">
                          <div className="w-5 h-5 rounded-full border border-white/20" style={{ backgroundColor: color.id }} /><span className="text-sm">{color.name}</span>
                          <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/subtitle-color/${encodeURIComponent(color.id)}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button onClick={async () => { const name = prompt('Farb-Name (z.B. "Blau"):'); if (name) { const hex = prompt('Hex-Code (z.B. "#0000FF"):', '#'); if (hex && hex !== '#') { try { await fetch(`${API_URL}/api/pricing/config/subtitle-color`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: hex, name }) }); } catch(e){} fetchPricingConfig(true); } } }} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-dashed border-white/20 hover:text-white text-gray-400"><Plus className="w-4 h-4" /> Farbe</button>
                    </div>
                  </div>

                  {/* ===== Section 6: Rechner-Bereiche + Rohmaterial-Staffelung (side-by-side) ===== */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><Sliders className="w-5 h-5" style={{ color: '#5C63FF' }} />Rechner-Bereiche</h3>
                      <p className="text-xs text-gray-500 mb-4">Min/Max Werte für die Slider im Rechner</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-xs text-gray-400 mb-1">Min Videolänge (Min)</label><input type="number" value={pricingConfig.video_length_min ?? 1} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, video_length_min: val }), () => fetch(`${API_URL}/api/pricing/config/slider-ranges`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ video_length_min: val }) })); }} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" /></div>
                        <div><label className="block text-xs text-gray-400 mb-1">Max Videolänge (Min)</label><input type="number" value={pricingConfig.video_length_max ?? 60} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, video_length_max: val }), () => fetch(`${API_URL}/api/pricing/config/slider-ranges`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ video_length_max: val }) })); }} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" /></div>
                        <div><label className="block text-xs text-gray-400 mb-1">Min Rohmaterial (Min)</label><input type="number" value={pricingConfig.raw_material_min ?? 5} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, raw_material_min: val }), () => fetch(`${API_URL}/api/pricing/config/slider-ranges`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ raw_material_min: val }) })); }} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" /></div>
                        <div><label className="block text-xs text-gray-400 mb-1">Max Rohmaterial (Min)</label><input type="number" value={pricingConfig.raw_material_max ?? 120} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, raw_material_max: val }), () => fetch(`${API_URL}/api/pricing/config/slider-ranges`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ raw_material_max: val }) })); }} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" /></div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" style={{ color: '#5C63FF' }} />Rohmaterial-Staffelung</h3>
                      <div className="space-y-2">
                        <p className="text-xs text-gray-500 mb-2">Rohmaterial-Dauer → Preisfaktor</p>
                        {pricingConfig.default_raw_material_tiers?.map((tier, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5"><span className="text-sm text-gray-400">Bis</span><input type="number" value={tier.up_to_minutes} onChange={(e) => { const t = [...pricingConfig.default_raw_material_tiers]; t[i] = { ...t[i], up_to_minutes: e.target.value === '' ? 9999 : parseInt(e.target.value) }; handleUpdateTier('raw_material', t); }} className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-center text-sm" /><span className="text-sm text-gray-400">Min:</span><span className="text-sm" style={{ color: '#5C63FF' }}>x</span><input type="number" step="0.05" value={tier.factor} onChange={(e) => { const t = [...pricingConfig.default_raw_material_tiers]; t[i] = { ...t[i], factor: e.target.value === '' ? '' : parseFloat(e.target.value) }; handleUpdateTier('raw_material', t); }} className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-center text-sm" /><button onClick={() => handleUpdateTier('raw_material', pricingConfig.default_raw_material_tiers.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button></div>
                        ))}
                        <button onClick={() => handleUpdateTier('raw_material', [...(pricingConfig.default_raw_material_tiers || []), { up_to_minutes: 60, factor: 1.5 }])} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white">+ Neue Stufe</button>
                        <p className="text-xs text-gray-500 mt-4 mb-2">Video-Länge → Preisfaktor</p>
                        {pricingConfig.default_output_length_tiers?.map((tier, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5"><span className="text-sm text-gray-400">Bis</span><input type="number" value={tier.up_to_minutes} onChange={(e) => { const t = [...pricingConfig.default_output_length_tiers]; t[i] = { ...t[i], up_to_minutes: e.target.value === '' ? 9999 : parseInt(e.target.value) }; handleUpdateTier('output_length', t); }} className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-center text-sm" /><span className="text-sm text-gray-400">Min:</span><span className="text-sm" style={{ color: '#5C63FF' }}>x</span><input type="number" step="0.05" value={tier.factor} onChange={(e) => { const t = [...pricingConfig.default_output_length_tiers]; t[i] = { ...t[i], factor: e.target.value === '' ? '' : parseFloat(e.target.value) }; handleUpdateTier('output_length', t); }} className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-center text-sm" /><button onClick={() => handleUpdateTier('output_length', pricingConfig.default_output_length_tiers.filter((_, idx) => idx !== i))} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button></div>
                        ))}
                        <button onClick={() => handleUpdateTier('output_length', [...(pricingConfig.default_output_length_tiers || []), { up_to_minutes: 15, factor: 1.5 }])} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white">+ Neue Stufe</button>
                      </div>
                    </div>
                  </div>

                  {/* ===== Section 7: Rush Delivery + Extra Revisionen (side-by-side) ===== */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><Zap className="w-5 h-5" style={{ color: '#5C63FF' }} />Rush Delivery Optionen</h3>
                      <div className="space-y-2 mb-4">
                        {pricingConfig.rush_delivery_options?.filter(r => r.id !== 'none').sort((a, b) => a.order - b.order).map((rd) => (
                          <div key={rd.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <input type="text" value={rd.name} onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, rush_delivery_options: prev.rush_delivery_options.map(r => r.id === rd.id ? { ...r, name: val } : r) }), () => fetch(`${API_URL}/api/pricing/config/rush-delivery/${rd.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...rd, name: val }) })); }} className="flex-1 text-sm font-medium bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                            <div className="flex items-center gap-1"><span className="text-xs" style={{ color: '#5C63FF' }}>%</span><input type="number" step="5" value={rd.percentage || 0} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, rush_delivery_options: prev.rush_delivery_options.map(r => r.id === rd.id ? { ...r, percentage: val } : r) }), () => fetch(`${API_URL}/api/pricing/config/rush-delivery/${rd.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...rd, percentage: val }) })); }} className="w-16 px-2 py-1 rounded-lg text-center text-sm" style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)', color: '#5C63FF', border: '1px solid rgba(92, 99, 255, 0.3)' }} /></div>
                            <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/rush-delivery/${rd.id}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <button onClick={async () => { const name = prompt('Neue Rush-Option:'); if (name) { const pct = parseFloat(prompt('Aufschlag (%):', '40')) || 0; try { await fetch(`${API_URL}/api/pricing/config/rush-delivery`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, percentage: pct }) }); } catch(e){} fetchPricingConfig(true); } }} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white">+ Rush-Option hinzufügen</button>
                    </div>
                    <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                      <h3 className="font-bold mb-4 flex items-center gap-2"><RefreshCw className="w-5 h-5" style={{ color: '#5C63FF' }} />Extra Revisionen</h3>
                      <div className="space-y-2 mb-4">
                        {pricingConfig.extra_revision_options?.filter(e => e.id !== 'none').sort((a, b) => a.order - b.order).map((er) => (
                          <div key={er.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                            <input type="text" value={er.name} onChange={(e) => { const val = e.target.value; updatePricingField(prev => ({ ...prev, extra_revision_options: prev.extra_revision_options.map(r => r.id === er.id ? { ...r, name: val } : r) }), () => fetch(`${API_URL}/api/pricing/config/extra-revision/${er.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...er, name: val }) })); }} className="flex-1 text-sm font-medium bg-transparent border-b border-transparent hover:border-white/20 focus:outline-none" style={{ borderBottomColor: 'transparent' }} onFocus={(e) => e.target.style.borderBottomColor = '#5C63FF'} onBlur={(e) => e.target.style.borderBottomColor = 'transparent'} />
                            <div className="flex items-center gap-1"><span className="text-xs" style={{ color: '#5C63FF' }}>€</span><input type="number" step="5" value={er.flat_price || 0} onChange={(e) => { const val = e.target.value === '' ? '' : parseFloat(e.target.value); updatePricingField(prev => ({ ...prev, extra_revision_options: prev.extra_revision_options.map(r => r.id === er.id ? { ...r, flat_price: val } : r) }), () => fetch(`${API_URL}/api/pricing/config/extra-revision/${er.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...er, flat_price: val }) })); }} className="w-16 px-2 py-1 rounded-lg text-center text-sm" style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)', color: '#5C63FF', border: '1px solid rgba(92, 99, 255, 0.3)' }} /></div>
                            <button onClick={async () => { try { await fetch(`${API_URL}/api/pricing/config/extra-revision/${er.id}`, { method: 'DELETE' }); } catch(e){} fetchPricingConfig(true); }} className="p-1 rounded hover:bg-red-500/20 text-red-400"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                      <button onClick={async () => { const name = prompt('Neue Revisions-Option:'); if (name) { const price = parseFloat(prompt('Preis (€):', '30')) || 0; try { await fetch(`${API_URL}/api/pricing/config/extra-revision`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, flat_price: price }) }); } catch(e){} fetchPricingConfig(true); } }} className="w-full py-2 border border-dashed border-white/20 rounded-lg text-sm text-gray-400 hover:text-white">+ Revisions-Option hinzufügen</button>
                    </div>
                  </div>

                  {/* ===== Section 8: LIVE-VORSCHAU ===== */}
                  <div className="rounded-2xl border p-0 overflow-hidden" style={{ borderColor: showPreview ? '#5C63FF' : 'rgba(255,255,255,0.1)', backgroundColor: '#111' }}>
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors"
                      data-testid="preview-toggle"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                          <Eye className="w-4 h-4" style={{ color: '#5C63FF' }} />
                        </div>
                        <div className="text-left">
                          <span className="font-bold text-sm">Live-Vorschau</span>
                          <p className="text-xs text-gray-500">Preisberechnung in Echtzeit testen</p>
                        </div>
                      </div>
                      {showPreview ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    {showPreview && (() => {
                      // Calculator logic using current pricingConfig
                      const pc = pricingConfig;
                      const v = previewVideo;
                      const bp = pc?.default_base_price || 80;
                      
                      // Raw material factor from tiers
                      const getRmFactor = (mins) => {
                        const tiers = pc?.default_raw_material_tiers || [];
                        if (tiers.length === 0) {
                          if (mins <= 15) return 0.85; if (mins <= 30) return 1.0; if (mins <= 60) return 1.15; return 1.3;
                        }
                        const sorted = [...tiers].sort((a, b) => a.up_to_minutes - b.up_to_minutes);
                        for (const t of sorted) { if (mins <= t.up_to_minutes) return t.factor; }
                        return sorted[sorted.length - 1]?.factor || 1.0;
                      };
                      
                      const rmFactor = getRmFactor(v.rawMaterialMinutes);
                      const brollFactor = v.needsBRoll ? 1.15 : 1.0;
                      const totalMinutes = v.videoCount * v.minutesPerVideo;
                      const baseTotal = totalMinutes * bp * rmFactor * brollFactor;
                      
                      // Subtitle cost
                      const st = pc?.subtitle_types?.find(s => s.id === v.subtitleType);
                      const subtitleCost = st ? baseTotal * ((st.flat_price || 0) / 100) : 0;
                      
                      // Rush cost
                      const rush = pc?.rush_delivery_options?.find(r => r.id === v.rushDelivery);
                      const rushCost = rush ? baseTotal * ((rush.percentage || 0) / 100) : 0;
                      
                      // Revision cost
                      const rev = pc?.extra_revision_options?.find(r => r.id === v.extraRevisions);
                      const revCost = rev ? (rev.flat_price || 0) * v.videoCount : 0;
                      
                      // Addon cost
                      let addonCost = 0;
                      if (pc?.addon_categories) {
                        pc.addon_categories.forEach(cat => {
                          cat.addons?.forEach(addon => {
                            if (v.addons[addon.id]) {
                              if (addon.percentage != null) addonCost += baseTotal * (addon.percentage / 100);
                              else if (addon.flat_price != null) addonCost += addon.flat_price * v.videoCount;
                            }
                          });
                        });
                      }
                      
                      const subtotal = baseTotal + subtitleCost + rushCost + revCost + addonCost;
                      
                      // Margin
                      let marginPct = 20;
                      if (marginTiers.length > 0) {
                        const sorted = [...marginTiers].sort((a, b) => b.min_amount - a.min_amount);
                        for (const t of sorted) { if (subtotal >= t.min_amount) { marginPct = t.margin_percent; break; } }
                      }
                      const marginAmt = subtotal * (marginPct / 100);
                      const total = subtotal + marginAmt;
                      
                      return (
                        <div className="px-6 pb-6 space-y-5 border-t border-white/10">
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 pt-5">
                            {/* Left: Config */}
                            <div className="lg:col-span-2 space-y-4">
                              {/* Row 1: Format + Type */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Format</label>
                                  <select value={v.format} onChange={(e) => setPreviewVideo({...v, format: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
                                    {pc?.video_formats?.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Videotyp</label>
                                  <select value={v.videoType} onChange={(e) => setPreviewVideo({...v, videoType: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
                                    {pc?.video_types?.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                  </select>
                                </div>
                              </div>
                              
                              {/* Row 2: Language + Subtitle */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Sprache</label>
                                  <select value={v.language} onChange={(e) => setPreviewVideo({...v, language: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
                                    {pc?.languages?.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Untertitel</label>
                                  <select value={v.subtitleType} onChange={(e) => setPreviewVideo({...v, subtitleType: e.target.value})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
                                    {pc?.subtitle_types?.map(s => <option key={s.id} value={s.id}>{s.name}{s.flat_price ? ` (+${s.flat_price}%)` : ''}</option>)}
                                  </select>
                                </div>
                              </div>
                              
                              {/* Row 3: Sliders */}
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Anzahl Videos</label>
                                  <input type="number" min="1" max="50" value={v.videoCount} onChange={(e) => setPreviewVideo({...v, videoCount: Math.max(1, parseInt(e.target.value) || 1)})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Min/Video</label>
                                  <input type="number" min="1" max="60" value={v.minutesPerVideo} onChange={(e) => setPreviewVideo({...v, minutesPerVideo: Math.max(1, parseInt(e.target.value) || 1)})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Rohmaterial (Min)</label>
                                  <input type="number" min="5" max="600" value={v.rawMaterialMinutes} onChange={(e) => setPreviewVideo({...v, rawMaterialMinutes: Math.max(5, parseInt(e.target.value) || 5)})} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
                                </div>
                              </div>
                              
                              {/* Row 4: Options */}
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                                    <input type="checkbox" checked={v.needsBRoll} onChange={(e) => setPreviewVideo({...v, needsBRoll: e.target.checked})} className="rounded" />
                                    B-Roll (+15%)
                                  </label>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Rush</label>
                                  <select value={v.rushDelivery} onChange={(e) => setPreviewVideo({...v, rushDelivery: e.target.value})} className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs">
                                    {pc?.rush_delivery_options?.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs text-gray-500 mb-1">Revisionen</label>
                                  <select value={v.extraRevisions} onChange={(e) => setPreviewVideo({...v, extraRevisions: e.target.value})} className="w-full px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs">
                                    {pc?.extra_revision_options?.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                  </select>
                                </div>
                              </div>
                              
                              {/* Row 5: Addons */}
                              {pc?.addon_categories?.filter(c => c.id !== 'workflow' && c.id !== 'broll_assets' && c.id !== 'upload_publishing').length > 0 && (
                                <div>
                                  <label className="block text-xs text-gray-500 mb-2">Addons</label>
                                  <div className="flex flex-wrap gap-2">
                                    {pc.addon_categories.filter(c => c.id !== 'workflow' && c.id !== 'broll_assets' && c.id !== 'upload_publishing').flatMap(c => c.addons || []).map(addon => (
                                      <button
                                        key={addon.id}
                                        onClick={() => setPreviewVideo({...v, addons: {...v.addons, [addon.id]: !v.addons[addon.id]}})}
                                        className="px-3 py-1.5 rounded-lg border text-xs transition-all"
                                        style={{
                                          backgroundColor: v.addons[addon.id] ? 'rgba(92, 99, 255, 0.15)' : 'rgba(255,255,255,0.03)',
                                          borderColor: v.addons[addon.id] ? '#5C63FF' : 'rgba(255,255,255,0.1)',
                                          color: v.addons[addon.id] ? '#5C63FF' : '#9ca3af'
                                        }}
                                      >
                                        {addon.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Right: Price Breakdown */}
                            <div className="rounded-xl p-5 space-y-3" style={{ backgroundColor: 'rgba(92, 99, 255, 0.05)', border: '1px solid rgba(92, 99, 255, 0.2)' }} data-testid="preview-price-breakdown">
                              <h4 className="font-bold text-sm flex items-center gap-2" style={{ color: '#5C63FF' }}>
                                <Calculator className="w-4 h-4" />
                                Preiskalkulation
                              </h4>
                              
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between text-gray-400">
                                  <span>{v.videoCount}x {v.minutesPerVideo} Min</span>
                                  <span>= {totalMinutes} Min</span>
                                </div>
                                <div className="flex justify-between text-gray-400">
                                  <span>Basispreis</span>
                                  <span>€{bp}/Min</span>
                                </div>
                                <div className="flex justify-between text-gray-400">
                                  <span>Rohmaterial-Faktor</span>
                                  <span>x{rmFactor.toFixed(2)}</span>
                                </div>
                                {v.needsBRoll && (
                                  <div className="flex justify-between text-gray-400">
                                    <span>B-Roll</span>
                                    <span>x1.15</span>
                                  </div>
                                )}
                                <div className="border-t border-white/10 pt-2 flex justify-between">
                                  <span className="text-gray-300">Basis</span>
                                  <span className="text-white font-medium">€{baseTotal.toFixed(2)}</span>
                                </div>
                                
                                {subtitleCost > 0 && (
                                  <div className="flex justify-between text-gray-400">
                                    <span>Untertitel</span>
                                    <span>+€{subtitleCost.toFixed(2)}</span>
                                  </div>
                                )}
                                {rushCost > 0 && (
                                  <div className="flex justify-between text-gray-400">
                                    <span>Rush Delivery</span>
                                    <span>+€{rushCost.toFixed(2)}</span>
                                  </div>
                                )}
                                {revCost > 0 && (
                                  <div className="flex justify-between text-gray-400">
                                    <span>Extra Revisionen</span>
                                    <span>+€{revCost.toFixed(2)}</span>
                                  </div>
                                )}
                                {addonCost > 0 && (
                                  <div className="flex justify-between text-gray-400">
                                    <span>Addons</span>
                                    <span>+€{addonCost.toFixed(2)}</span>
                                  </div>
                                )}
                                
                                <div className="border-t border-white/10 pt-2 flex justify-between">
                                  <span className="text-gray-300">Netto</span>
                                  <span className="text-white font-medium">€{subtotal.toFixed(2)}</span>
                                </div>
                                
                                <div className="flex justify-between text-gray-400">
                                  <span>Marge ({marginPct}%)</span>
                                  <span>+€{marginAmt.toFixed(2)}</span>
                                </div>
                                
                                <div className="border-t-2 pt-3 flex justify-between items-center" style={{ borderColor: '#5C63FF' }}>
                                  <span className="font-bold text-white">Kundenpreis</span>
                                  <span className="text-xl font-bold" style={{ color: '#5C63FF' }}>€{total.toFixed(2)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Preiskonfiguration konnte nicht geladen werden</p>
                  <button onClick={fetchPricingConfig} className="btn-primary mt-4">
                    Erneut versuchen
                  </button>
                </div>
              )}
            </>
          ) : activeTab === 'chatbot' ? (
            <>
              {/* Chatbot / FAQ Management */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <MessageCircle className="w-6 h-6 inline mr-2" style={{ color: '#5C63FF' }} />
                  Chatbot & FAQ verwalten
                </h2>
                <button 
                  onClick={() => { setEditingFaq(null); setNewFaq({ question: '', answer: '', keywords: '', category: 'Allgemein', is_active: true }); setShowFaqModal(true); }}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Neue FAQ
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Chatbot Settings */}
                <div className="lg:col-span-1">
                  <div className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }}>
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Settings className="w-5 h-5" style={{ color: '#5C63FF' }} />
                      Chatbot-Einstellungen
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Begrüßung</label>
                        <input
                          type="text"
                          value={chatbotSettings.welcome_message}
                          onChange={(e) => setChatbotSettings({...chatbotSettings, welcome_message: e.target.value})}
                          className="input-field"
                          placeholder="Hallo! Wie kann ich dir helfen?"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Fallback-Nachricht</label>
                        <textarea
                          value={chatbotSettings.fallback_message}
                          onChange={(e) => setChatbotSettings({...chatbotSettings, fallback_message: e.target.value})}
                          className="input-field"
                          rows={3}
                          placeholder="Das kann ich leider nicht beantworten..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">E-Mail-Hinweis</label>
                        <input
                          type="text"
                          value={chatbotSettings.email_prompt}
                          onChange={(e) => setChatbotSettings({...chatbotSettings, email_prompt: e.target.value})}
                          className="input-field"
                          placeholder="Schreib uns an info@..."
                        />
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={chatbotSettings.is_active}
                          onChange={(e) => setChatbotSettings({...chatbotSettings, is_active: e.target.checked})}
                          className="w-5 h-5 rounded"
                        />
                        <label className="text-sm">Chatbot aktiv</label>
                      </div>
                      
                      <button
                        onClick={handleSaveChatbotSettings}
                        className="btn-primary w-full flex items-center justify-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Einstellungen speichern
                      </button>
                    </div>
                  </div>
                </div>

                {/* FAQ List */}
                <div className="lg:col-span-2">
                  <div className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="p-4 border-b border-white/10 flex items-center justify-between">
                      <span className="font-medium">FAQ-Einträge ({faqs.length})</span>
                    </div>
                    
                    {loadingFaqs ? (
                      <div className="p-8 text-center">
                        <div className="w-6 h-6 border-2 border-white/20 border-t-[#5C63FF] rounded-full animate-spin mx-auto" />
                      </div>
                    ) : faqs.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        <HelpCircle className="w-12 h-12 mx-auto mb-3 text-gray-500" />
                        <p>Noch keine FAQs vorhanden</p>
                        <p className="text-sm">Erstelle deine erste FAQ für den Chatbot</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-white/5">
                        {faqs.map((faq) => (
                          <div key={faq.id} className="p-4 hover:bg-white/5 transition-colors">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`w-2 h-2 rounded-full ${faq.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                                  <span className="font-medium">{faq.question}</span>
                                </div>
                                <p className="text-sm text-gray-400 line-clamp-2">{faq.answer}</p>
                                {faq.keywords?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {faq.keywords.map((kw, i) => (
                                      <span key={i} className="px-2 py-0.5 rounded bg-white/5 text-xs text-gray-400">{kw}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingFaq(faq);
                                    setNewFaq({
                                      question: faq.question,
                                      answer: faq.answer,
                                      keywords: faq.keywords?.join(', ') || '',
                                      category: faq.category || 'Allgemein',
                                      is_active: faq.is_active
                                    });
                                    setShowFaqModal(true);
                                  }}
                                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteFaq(faq.id)}
                                  className="p-2 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* FAQ Modal */}
              {showFaqModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="rounded-2xl border border-white/10 p-6 w-full max-w-lg" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold">
                        {editingFaq ? 'FAQ bearbeiten' : 'Neue FAQ erstellen'}
                      </h3>
                      <button onClick={() => { setShowFaqModal(false); setEditingFaq(null); }}>
                        <X className="w-6 h-6 text-gray-400 hover:text-white" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Frage *</label>
                        <input
                          type="text"
                          value={newFaq.question}
                          onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
                          className="input-field"
                          placeholder="Wie funktioniert das?"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Antwort *</label>
                        <textarea
                          value={newFaq.answer}
                          onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
                          className="input-field"
                          rows={4}
                          placeholder="Die Antwort auf die Frage..."
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Keywords (kommagetrennt)</label>
                        <input
                          type="text"
                          value={newFaq.keywords}
                          onChange={(e) => setNewFaq({...newFaq, keywords: e.target.value})}
                          className="input-field"
                          placeholder="preis, kosten, ablauf"
                        />
                        <p className="text-xs text-gray-500 mt-1">Hilft dem Bot, die richtige Antwort zu finden</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Kategorie</label>
                        <select
                          value={newFaq.category}
                          onChange={(e) => setNewFaq({...newFaq, category: e.target.value})}
                          className="input-field"
                        >
                          <option value="Allgemein">Allgemein</option>
                          <option value="Preise">Preise</option>
                          <option value="Ablauf">Ablauf</option>
                          <option value="Freelancer">Freelancer</option>
                          <option value="Kunden">Kunden</option>
                          <option value="Technisch">Technisch</option>
                        </select>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={newFaq.is_active}
                          onChange={(e) => setNewFaq({...newFaq, is_active: e.target.checked})}
                          className="w-5 h-5 rounded"
                        />
                        <label className="text-sm">Aktiv</label>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => { setShowFaqModal(false); setEditingFaq(null); }}
                        className="btn-secondary flex-1"
                      >
                        Abbrechen
                      </button>
                      <button
                        onClick={handleSaveFaq}
                        className="btn-primary flex-1 flex items-center justify-center gap-2"
                      >
                        <Save className="w-4 h-4" />
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : activeTab === 'teams' ? (
            <>
              {/* Freelancer Tab - EINE einheitliche Liste */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Freelancer verwalten
                </h2>
                {/* Freelancer werden nur über den Bewerbungsflow erstellt */}
              </div>

              {/* Freelancer Formular Modal - nur für Bearbeiten */}
              {showTeamForm && editingTeam && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                  <div className="rounded-2xl border border-white/10 p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" style={{ backgroundColor: '#1a1a1a' }}>
                    <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        Freelancer-Profil bearbeiten
                      </h3>
                      <button onClick={() => setShowTeamForm(false)}>
                        <X className="w-6 h-6 text-gray-400 hover:text-white" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Linke Spalte - Grunddaten */}
                      <div className="space-y-4">
                        <h4 className="font-bold text-purple-400 flex items-center gap-2">
                          <User className="w-4 h-4" /> Grunddaten
                        </h4>
                        
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Name *</label>
                          <input
                            type="text"
                            value={teamForm.name}
                            onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                            className="input-field"
                            placeholder="Freelancer Name"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">E-Mail</label>
                          <input
                            type="email"
                            value={teamForm.email}
                            onChange={(e) => setTeamForm({...teamForm, email: e.target.value})}
                            className="input-field"
                            placeholder="email@example.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Telefon</label>
                          <input
                            type="tel"
                            value={teamForm.phone}
                            onChange={(e) => setTeamForm({...teamForm, phone: e.target.value})}
                            className="input-field"
                            placeholder="+49 123 456789"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Stadt</label>
                          <input
                            type="text"
                            value={teamForm.location?.city || ''}
                            onChange={(e) => setTeamForm({...teamForm, location: {...(teamForm.location || {}), city: e.target.value}})}
                            className="input-field"
                            placeholder="z.B. Berlin"
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Beschreibung / Über mich</label>
                          <textarea
                            value={teamForm.description}
                            onChange={(e) => setTeamForm({...teamForm, description: e.target.value})}
                            className="input-field"
                            rows={4}
                            placeholder="Kurze Beschreibung..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Avatar URL</label>
                          <input
                            type="text"
                            value={teamForm.avatar}
                            onChange={(e) => setTeamForm({...teamForm, avatar: e.target.value})}
                            className="input-field"
                            placeholder="https://..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Basispreis pro Minute (€)</label>
                          <input
                            type="number"
                            value={teamForm.prices?.basePerMinute || 80}
                            onChange={(e) => setTeamForm({...teamForm, prices: {...teamForm.prices, basePerMinute: parseFloat(e.target.value)}})}
                            className="input-field"
                            placeholder="80"
                          />
                        </div>

                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={teamForm.is_boosted}
                            onChange={(e) => setTeamForm({...teamForm, is_boosted: e.target.checked})}
                            className="w-5 h-5 rounded"
                          />
                          <label className="text-sm">
                            <Star className="w-4 h-4 inline mr-1 text-yellow-500" />
                            Featured (oben anzeigen)
                          </label>
                        </div>
                      </div>

                      {/* Rechte Spalte - Erweiterte Daten */}
                      <div className="space-y-4">
                        {/* Spezialisierungen */}
                        <div>
                          <h4 className="font-bold text-purple-400 flex items-center gap-2 mb-3">
                            <Tag className="w-4 h-4" /> Spezialisierungen
                          </h4>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {teamForm.specializations?.map((spec, i) => (
                              <span key={i} className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm flex items-center gap-2">
                                {spec}
                                <button onClick={() => setTeamForm({
                                  ...teamForm, 
                                  specializations: teamForm.specializations.filter((_, idx) => idx !== i)
                                })} className="hover:text-red-400">×</button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newSpecialization}
                              onChange={(e) => setNewSpecialization(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newSpecialization) {
                                  setTeamForm({...teamForm, specializations: [...(teamForm.specializations || []), newSpecialization]});
                                  setNewSpecialization('');
                                }
                              }}
                              className="input-field flex-1"
                              placeholder="z.B. Color Grading"
                            />
                            <button
                              onClick={() => {
                                if (newSpecialization) {
                                  setTeamForm({...teamForm, specializations: [...(teamForm.specializations || []), newSpecialization]});
                                  setNewSpecialization('');
                                }
                              }}
                              className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400"
                            >+</button>
                          </div>
                        </div>

                        {/* Software & Tools */}
                        <div>
                          <h4 className="font-bold text-green-400 flex items-center gap-2 mb-3">
                            <Settings className="w-4 h-4" /> Software & Tools
                          </h4>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {teamForm.software_skills?.map((skill, i) => (
                              <span key={i} className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-2">
                                {skill}
                                <button onClick={() => setTeamForm({
                                  ...teamForm, 
                                  software_skills: teamForm.software_skills.filter((_, idx) => idx !== i)
                                })} className="hover:text-red-400">×</button>
                              </span>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={newSoftwareSkill}
                              onChange={(e) => setNewSoftwareSkill(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newSoftwareSkill) {
                                  setTeamForm({...teamForm, software_skills: [...(teamForm.software_skills || []), newSoftwareSkill]});
                                  setNewSoftwareSkill('');
                                }
                              }}
                              className="input-field flex-1"
                              placeholder="z.B. Adobe Premiere"
                            />
                            <button
                              onClick={() => {
                                if (newSoftwareSkill) {
                                  setTeamForm({...teamForm, software_skills: [...(teamForm.software_skills || []), newSoftwareSkill]});
                                  setNewSoftwareSkill('');
                                }
                              }}
                              className="px-4 py-2 rounded-lg bg-green-500/20 text-green-400"
                            >+</button>
                          </div>
                        </div>

                        {/* Portfolio Links */}
                        <div>
                          <h4 className="font-bold text-blue-400 flex items-center gap-2 mb-3">
                            <Video className="w-4 h-4" /> Portfolio Links
                          </h4>
                          <div className="space-y-2 mb-2">
                            {teamForm.portfolio_links?.map((link, i) => (
                              <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                                <Link2 className="w-4 h-4 text-blue-400" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{link.title}</p>
                                  <p className="text-xs text-gray-500 truncate">{link.url}</p>
                                </div>
                                <button onClick={() => setTeamForm({
                                  ...teamForm, 
                                  portfolio_links: teamForm.portfolio_links.filter((_, idx) => idx !== i)
                                })} className="text-red-400 hover:text-red-300">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={newPortfolioLink.title}
                              onChange={(e) => setNewPortfolioLink({...newPortfolioLink, title: e.target.value})}
                              className="input-field"
                              placeholder="Titel (z.B. 'Imagefilm XYZ')"
                            />
                            <div className="flex gap-2">
                              <input
                                type="url"
                                value={newPortfolioLink.url}
                                onChange={(e) => setNewPortfolioLink({...newPortfolioLink, url: e.target.value})}
                                className="input-field flex-1"
                                placeholder="https://..."
                              />
                              <button
                                onClick={() => {
                                  if (newPortfolioLink.title && newPortfolioLink.url) {
                                    setTeamForm({...teamForm, portfolio_links: [...(teamForm.portfolio_links || []), {...newPortfolioLink}]});
                                    setNewPortfolioLink({ title: '', url: '' });
                                  }
                                }}
                                className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400"
                              >+</button>
                            </div>
                          </div>
                        </div>

                        {/* Social Media */}
                        <div>
                          <h4 className="font-bold text-pink-400 flex items-center gap-2 mb-3">
                            <Link2 className="w-4 h-4" /> Social Media
                          </h4>
                          <div className="space-y-2">
                            <input
                              type="url"
                              value={teamForm.social_links?.instagram || ''}
                              onChange={(e) => setTeamForm({...teamForm, social_links: {...(teamForm.social_links || {}), instagram: e.target.value}})}
                              className="input-field"
                              placeholder="Instagram URL"
                            />
                            <input
                              type="url"
                              value={teamForm.social_links?.youtube || ''}
                              onChange={(e) => setTeamForm({...teamForm, social_links: {...(teamForm.social_links || {}), youtube: e.target.value}})}
                              className="input-field"
                              placeholder="YouTube URL"
                            />
                            <input
                              type="url"
                              value={teamForm.social_links?.twitter || ''}
                              onChange={(e) => setTeamForm({...teamForm, social_links: {...(teamForm.social_links || {}), twitter: e.target.value}})}
                              className="input-field"
                              placeholder="Twitter / X URL"
                            />
                            <input
                              type="url"
                              value={teamForm.social_links?.website || ''}
                              onChange={(e) => setTeamForm({...teamForm, social_links: {...(teamForm.social_links || {}), website: e.target.value}})}
                              className="input-field"
                              placeholder="Website URL"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleSaveTeam}
                      className="w-full btn-primary mt-6"
                    >
                      {editingTeam ? 'Änderungen speichern' : 'Freelancer erstellen'}
                    </button>
                  </div>
                </div>
              )}

              {/* Freelancer Liste - zeigt ALLE "teams" als Freelancer */}
              {teams.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Noch keine Freelancer vorhanden</p>
                  <p className="text-sm text-gray-500 mt-2">Freelancer werden über den Bewerbungsflow erstellt.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map((freelancer) => (
                    <div key={freelancer.id} className="rounded-2xl border border-white/10 p-5" style={{ backgroundColor: '#1a1a1a' }} data-testid={`freelancer-card-${freelancer.id}`}>
                      <div className="flex items-center gap-4 mb-4">
                        {freelancer.avatar ? (
                          <img src={freelancer.avatar} alt={freelancer.name} className="w-14 h-14 rounded-full object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}>
                            <span className="text-xl font-bold" style={{ color: '#5C63FF' }}>
                              {freelancer.name?.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{freelancer.name}</h3>
                            {freelancer.is_boosted && (
                              <Star className="w-4 h-4 text-yellow-500" />
                            )}
                          </div>
                          <p className="text-sm text-gray-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {freelancer.location?.city || 'Keine Stadt'}
                          </p>
                        </div>
                      </div>
                      
                      {freelancer.description && (
                        <p className="text-sm text-gray-400 mb-4 line-clamp-2">{freelancer.description}</p>
                      )}
                      
                      {/* Buttons: Bearbeiten, Löschen, Zugangsdaten senden */}
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditTeam(freelancer)}
                            className="flex-1 px-3 py-2 rounded-lg text-sm bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 flex items-center justify-center gap-1"
                            data-testid={`edit-freelancer-${freelancer.id}`}
                          >
                            <Edit className="w-4 h-4" /> Bearbeiten
                          </button>
                          <button
                            onClick={() => handleDeleteTeam(freelancer.id)}
                            className="flex-1 px-3 py-2 rounded-lg text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center gap-1"
                            data-testid={`delete-freelancer-${freelancer.id}`}
                          >
                            <Trash2 className="w-4 h-4" /> Löschen
                          </button>
                        </div>
                        <button
                          onClick={async () => {
                            // Versuche erst über die Email des Teams/Freelancers
                            const email = freelancer.email || freelancer.contact_email;
                            if (!email) {
                              alert('Keine E-Mail-Adresse für diesen Freelancer hinterlegt. Bitte bearbeiten Sie den Eintrag und fügen Sie eine E-Mail hinzu.');
                              return;
                            }
                            try {
                              const res = await fetch(`${API_URL}/api/freelancer/resend-credentials?email=${encodeURIComponent(email)}`, {
                                method: 'POST'
                              });
                              if (res.ok) {
                                alert('Zugangsdaten wurden gesendet an ' + email);
                              } else {
                                const err = await res.json();
                                alert('Fehler: ' + (err.detail || 'Unbekannter Fehler'));
                              }
                            } catch (e) {
                              alert('Fehler beim Senden: ' + e.message);
                            }
                          }}
                          className="w-full px-3 py-2 rounded-lg text-sm bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 flex items-center justify-center gap-1"
                          data-testid={`send-credentials-${freelancer.id}`}
                        >
                          <Mail className="w-4 h-4" /> Zugangsdaten senden
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : activeTab === 'invoices' ? (
            <>
              {/* Invoices Management - Enhanced */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <FileText className="w-6 h-6 inline mr-2" style={{ color: '#5C63FF' }} />
                  Rechnungen
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setInvoiceSubTab('list'); fetchAllInvoices(); }}
                    data-testid="invoice-tab-list"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      invoiceSubTab === 'list' ? 'text-white' : 'text-gray-400 hover:text-white bg-white/5'
                    }`}
                    style={{ backgroundColor: invoiceSubTab === 'list' ? '#5C63FF' : undefined }}
                  >
                    <Receipt className="w-4 h-4 inline mr-1.5" />
                    Übersicht
                  </button>
                  <button
                    onClick={() => { setInvoiceSubTab('settings'); fetchInvoiceSettings(); }}
                    data-testid="invoice-tab-settings"
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      invoiceSubTab === 'settings' ? 'text-white' : 'text-gray-400 hover:text-white bg-white/5'
                    }`}
                    style={{ backgroundColor: invoiceSubTab === 'settings' ? '#5C63FF' : undefined }}
                  >
                    <Settings className="w-4 h-4 inline mr-1.5" />
                    Einstellungen
                  </button>
                </div>
              </div>

              {invoiceSubTab === 'list' ? (
                <>
                  {loadingInvoices ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                      <p className="text-gray-400">Rechnungen werden geladen...</p>
                    </div>
                  ) : allInvoices.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                      <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                      <h3 className="text-xl font-bold mb-2">Keine Rechnungen</h3>
                      <p className="text-gray-400">Es wurden noch keine Rechnungen erstellt.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {allInvoices.map((invoice) => {
                        const isPaid = invoice.status === 'paid';
                        const isOverdue = !isPaid && invoice.due_date && new Date(invoice.due_date) < new Date();
                        
                        return (
                          <div 
                            key={invoice.id} 
                            data-testid={`invoice-row-${invoice.id}`}
                            className={`rounded-xl border p-5 transition-all hover:border-white/20 ${
                              isPaid ? 'border-green-500/30 bg-green-500/5' 
                              : isOverdue ? 'border-red-500/30 bg-red-500/5' 
                              : 'border-white/10 bg-white/[0.02]'
                            }`}
                          >
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              {/* Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="font-bold truncate">
                                    {invoice.invoice_number || invoice.project_title || `#${invoice.id?.slice(-6)}`}
                                  </p>
                                  {invoice.invoice_number && invoice.project_title && (
                                    <span className="text-gray-500 text-sm truncate">— {invoice.project_title}</span>
                                  )}
                                  {isPaid && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400 whitespace-nowrap">Bezahlt</span>
                                  )}
                                  {isOverdue && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-400 whitespace-nowrap">Überfällig</span>
                                  )}
                                  {!isPaid && !isOverdue && (
                                    <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400 whitespace-nowrap">Offen</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-400">
                                  {invoice.customer_name || '-'} | Freelancer: {invoice.freelancer_name || '-'} | Fällig: {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('de-DE') : '-'}
                                </p>
                              </div>
                              {/* Amount + Actions */}
                              <div className="flex items-center gap-3 flex-wrap">
                                <p className="text-2xl font-bold whitespace-nowrap" style={{ color: isPaid ? '#22c55e' : '#5C63FF' }}>
                                  {invoice.amount?.toFixed(2) || '0.00'}€
                                </p>
                                <div className="flex items-center gap-1.5">
                                  {/* Detail view */}
                                  <button
                                    onClick={() => handleViewInvoiceDetail(invoice.id)}
                                    className="p-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                                    title="Details anzeigen"
                                    data-testid={`invoice-detail-${invoice.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {/* Assign number */}
                                  {!invoice.invoice_number && (
                                    <button
                                      onClick={() => handleAssignInvoiceNumber(invoice.id)}
                                      className="p-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                                      title="Rechnungsnummer zuweisen"
                                      data-testid={`invoice-assign-nr-${invoice.id}`}
                                    >
                                      <Hash className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* PDF Download */}
                                  <button
                                    onClick={() => handleDownloadPDF(invoice.id, invoice.invoice_number)}
                                    className="p-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                                    title="PDF herunterladen"
                                    data-testid={`invoice-pdf-${invoice.id}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {/* XML Download */}
                                  <button
                                    onClick={() => handleDownloadXML(invoice.id, invoice.invoice_number)}
                                    className="p-2 rounded-lg text-gray-300 hover:text-white transition-all"
                                    style={{ backgroundColor: 'rgba(92, 99, 255, 0.1)' }}
                                    title="XRechnung-XML herunterladen"
                                    data-testid={`invoice-xml-${invoice.id}`}
                                  >
                                    <FileText className="w-4 h-4" style={{ color: '#5C63FF' }} />
                                  </button>
                                  {/* Send to customer */}
                                  {!isPaid && (
                                    <button
                                      onClick={() => handleSendInvoiceToCustomer(invoice)}
                                      className="p-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white transition-all"
                                      title="Rechnung an Kunden senden"
                                      data-testid={`invoice-send-${invoice.id}`}
                                    >
                                      <Send className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* Mark as paid */}
                                  {!isPaid && (
                                    <button
                                      onClick={async () => {
                                        if (window.confirm('Als bezahlt markieren?')) {
                                          try {
                                            await fetch(`${API_URL}/api/freelancer/${invoice.freelancer_id}/invoices/${invoice.id}/paid`, { method: 'PUT' });
                                            toast.success('Als bezahlt markiert');
                                            fetchAllInvoices();
                                          } catch (e) {
                                            toast.error('Fehler');
                                          }
                                        }
                                      }}
                                      className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                                      title="Als bezahlt markieren"
                                      data-testid={`invoice-paid-${invoice.id}`}
                                    >
                                      <Check className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                /* Invoice Settings Sub-Tab */
                <>
                  {loadingInvoiceSettings ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                      <p className="text-gray-400">Einstellungen werden geladen...</p>
                    </div>
                  ) : invoiceSettings ? (
                    <div className="space-y-6">
                      {/* Company Info */}
                      <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                          <Building2 className="w-5 h-5" style={{ color: '#5C63FF' }} />
                          Firmendaten
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Firmenname</label>
                            <input
                              type="text"
                              value={invoiceSettings.company_name || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, company_name: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-company-name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">E-Mail</label>
                            <input
                              type="email"
                              value={invoiceSettings.email || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, email: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-email"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Adresse</label>
                            <input
                              type="text"
                              value={invoiceSettings.company_address || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, company_address: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-address"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">PLZ</label>
                              <input
                                type="text"
                                value={invoiceSettings.company_zip || ''}
                                onChange={e => setInvoiceSettings({...invoiceSettings, company_zip: e.target.value})}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                                data-testid="invoice-zip"
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Stadt</label>
                              <input
                                type="text"
                                value={invoiceSettings.company_city || ''}
                                onChange={e => setInvoiceSettings({...invoiceSettings, company_city: e.target.value})}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                                data-testid="invoice-city"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Telefon</label>
                            <input
                              type="text"
                              value={invoiceSettings.phone || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, phone: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-phone"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Website</label>
                            <input
                              type="text"
                              value={invoiceSettings.website || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, website: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-website"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Land (ISO-Code)</label>
                            <input
                              type="text"
                              value={invoiceSettings.company_country || 'DE'}
                              onChange={e => setInvoiceSettings({...invoiceSettings, company_country: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              maxLength={2}
                              data-testid="invoice-country"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Tax Info */}
                      <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                          <Receipt className="w-5 h-5" style={{ color: '#5C63FF' }} />
                          Steuer & Rechnungsformat
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Steuernummer</label>
                            <input
                              type="text"
                              value={invoiceSettings.tax_id || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, tax_id: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-tax-id"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">USt-IdNr.</label>
                            <input
                              type="text"
                              value={invoiceSettings.vat_id || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, vat_id: e.target.value})}
                              placeholder="DE123456789"
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-vat-id"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">USt.-Satz (%)</label>
                            <input
                              type="number"
                              value={invoiceSettings.tax_rate ?? 19}
                              onChange={e => setInvoiceSettings({...invoiceSettings, tax_rate: parseFloat(e.target.value) || 0})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-tax-rate"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Rechnungsnummer-Präfix</label>
                            <input
                              type="text"
                              value={invoiceSettings.invoice_prefix || 'RC'}
                              onChange={e => setInvoiceSettings({...invoiceSettings, invoice_prefix: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-prefix"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Zahlungsziel (Tage)</label>
                            <input
                              type="number"
                              value={invoiceSettings.payment_terms_days ?? 14}
                              onChange={e => setInvoiceSettings({...invoiceSettings, payment_terms_days: parseInt(e.target.value) || 0})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-payment-terms"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Bank Info */}
                      <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                          <CreditCard className="w-5 h-5" style={{ color: '#5C63FF' }} />
                          Bankverbindung
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">Bank</label>
                            <input
                              type="text"
                              value={invoiceSettings.bank_name || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, bank_name: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-bank-name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">IBAN</label>
                            <input
                              type="text"
                              value={invoiceSettings.iban || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, iban: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-iban"
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">BIC</label>
                            <input
                              type="text"
                              value={invoiceSettings.bic || ''}
                              onChange={e => setInvoiceSettings({...invoiceSettings, bic: e.target.value})}
                              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none"
                              data-testid="invoice-bic"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Notes Template */}
                      <div className="rounded-xl border border-white/10 p-6" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5" style={{ color: '#5C63FF' }} />
                          Rechnungsvorlage
                        </h3>
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Schlusstext / Hinweise</label>
                          <textarea
                            value={invoiceSettings.notes_template || ''}
                            onChange={e => setInvoiceSettings({...invoiceSettings, notes_template: e.target.value})}
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-[#5C63FF] focus:outline-none resize-none"
                            placeholder="z.B. Vielen Dank für Ihren Auftrag!"
                            data-testid="invoice-notes"
                          />
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end">
                        <button
                          onClick={saveInvoiceSettings}
                          disabled={savingInvoiceSettings}
                          className="px-6 py-3 rounded-xl text-white font-medium flex items-center gap-2 transition-all disabled:opacity-50"
                          style={{ backgroundColor: '#5C63FF' }}
                          data-testid="invoice-settings-save"
                        >
                          {savingInvoiceSettings ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                          Einstellungen speichern
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      Einstellungen konnten nicht geladen werden.
                    </div>
                  )}
                </>
              )}
            </>
          ) : activeTab === 'tags' ? (
            <>
              {/* Tags Management */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <Tag className="w-6 h-6 inline mr-2" style={{ color: '#5C63FF' }} />
                  Tags verwalten
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={addTagCategory}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white flex items-center gap-2 hover:opacity-90 transition-all"
                    style={{ backgroundColor: '#5C63FF' }}
                    data-testid="add-tag-category"
                  >
                    <Plus className="w-4 h-4" /> Kategorie
                  </button>
                  <button
                    onClick={saveTagCategories}
                    disabled={savingTags}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-white flex items-center gap-2 hover:opacity-90 transition-all disabled:opacity-50"
                    style={{ backgroundColor: '#22c55e' }}
                    data-testid="save-tags"
                  >
                    {savingTags ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                    Speichern
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-400 mb-6">
                Verwalte die Tags, die auf der Startseite als Filter und auf den Freelancer-Profilen angezeigt werden. Änderungen werden erst nach Klick auf "Speichern" übernommen.
              </p>

              {loadingTags ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                  <p className="text-gray-400">Tags werden geladen...</p>
                </div>
              ) : tagCategories.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Tag className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-xl font-bold mb-2">Keine Kategorien</h3>
                  <p className="text-gray-400 mb-4">Erstelle Tag-Kategorien für deine Freelancer-Profile.</p>
                  <button onClick={addTagCategory} className="px-4 py-2 rounded-xl text-white text-sm font-medium" style={{ backgroundColor: '#5C63FF' }}>
                    <Plus className="w-4 h-4 inline mr-1" /> Erste Kategorie erstellen
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {tagCategories.map((cat) => (
                    <div key={cat.id} className="rounded-2xl border border-white/10 p-6" style={{ backgroundColor: '#1a1a1a' }} data-testid={`tag-category-${cat.id}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Tag className="w-5 h-5" style={{ color: '#5C63FF' }} />
                          <input
                            type="text"
                            value={cat.name}
                            onChange={(e) => setTagCategories(prev => prev.map(c => c.id === cat.id ? { ...c, name: e.target.value } : c))}
                            className="text-lg font-bold bg-transparent border-b border-transparent hover:border-white/20 focus:border-[#5C63FF] focus:outline-none"
                            data-testid={`tag-category-name-${cat.id}`}
                          />
                        </div>
                        <button
                          onClick={() => removeTagCategory(cat.id)}
                          className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all"
                          title="Kategorie löschen"
                          data-testid={`tag-category-delete-${cat.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4 min-h-[40px]">
                        {(cat.tags || []).map((tag, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-white/5 border border-white/10 group hover:border-red-500/30 transition-all" data-testid={`tag-${cat.id}-${idx}`}>
                            {tag}
                            <button
                              onClick={() => removeTagFromCategory(cat.id, tag)}
                              className="w-4 h-4 rounded-full flex items-center justify-center opacity-40 group-hover:opacity-100 hover:bg-red-500/20 hover:text-red-400 transition-all"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        {(!cat.tags || cat.tags.length === 0) && (
                          <span className="text-sm text-gray-600 italic">Keine Tags in dieser Kategorie</span>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Neuen Tag hinzufügen..."
                          value={newTagInput[cat.id] || ''}
                          onChange={(e) => setNewTagInput(prev => ({ ...prev, [cat.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter') addTagToCategory(cat.id); }}
                          className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm focus:border-[#5C63FF] focus:outline-none"
                          data-testid={`tag-input-${cat.id}`}
                        />
                        <button
                          onClick={() => addTagToCategory(cat.id)}
                          className="px-3 py-2 rounded-lg text-sm font-medium text-white transition-all hover:opacity-80"
                          style={{ backgroundColor: '#5C63FF' }}
                          data-testid={`tag-add-${cat.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Projektanfragen
              </h2>

              {requests.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-12 text-center" style={{ backgroundColor: '#1a1a1a' }}>
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400">Noch keine Anfragen</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.map((request) => (
                    <div key={request.id} data-testid={`request-row-${request.id}`} className="rounded-2xl border border-white/10 overflow-hidden" style={{ backgroundColor: '#1a1a1a' }}>
                      <div className="p-5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                          <div>
                            <h3 className="font-bold text-lg">{request.name}</h3>
                            <p className="text-sm text-gray-400">{request.email}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              request.status === 'new' ? 'bg-yellow-500/20 text-yellow-400' :
                              request.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                              request.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                              request.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                              request.status === 'assigned' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {request.status === 'new' ? 'Neu' :
                               request.status === 'reviewed' ? 'Geprüft' :
                               request.status === 'accepted' ? 'Angenommen' :
                               request.status === 'rejected' ? 'Abgelehnt' :
                               request.status === 'assigned' ? 'Zugewiesen' :
                               request.status}
                            </span>
                            <span className="text-lg font-bold" style={{ color: '#5C63FF' }}>
                              €{request.calculated_price?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        </div>

                        {/* Quick info row */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {request.team_name && (
                            <span className="px-2 py-1 rounded-lg bg-white/5 text-xs text-gray-300">
                              <Users className="w-3 h-3 inline mr-1" /> {request.team_name}
                            </span>
                          )}
                          <span className="px-2 py-1 rounded-lg bg-white/5 text-xs text-gray-300">
                            <Video className="w-3 h-3 inline mr-1" />
                            {request.total_video_count || request.videos?.reduce((s,v) => s + (v.videoCount||1), 0) || request.video_count || 1} Videos, {request.minutes_per_video || '-'} Min/Video
                          </span>
                          {request.company && (
                            <span className="px-2 py-1 rounded-lg bg-white/5 text-xs text-gray-300">
                              <Briefcase className="w-3 h-3 inline mr-1" /> {request.company}
                            </span>
                          )}
                          {request.phone && (
                            <span className="px-2 py-1 rounded-lg bg-white/5 text-xs text-gray-300">
                              <Phone className="w-3 h-3 inline mr-1" /> {request.phone}
                            </span>
                          )}
                        </div>

                        {request.message && (
                          <div className="p-3 rounded-lg bg-white/5 mb-4">
                            <p className="text-sm text-gray-400">{request.message}</p>
                          </div>
                        )}

                        {/* Expand/Collapse Details */}
                        <button
                          onClick={() => toggleRequestExpand(request.id)}
                          className="w-full flex items-center justify-between px-4 py-2 rounded-lg bg-white/5 hover:bg-white/8 transition-all text-sm text-gray-300 mb-4"
                          data-testid={`request-expand-${request.id}`}
                        >
                          <span className="flex items-center gap-2">
                            <Layers className="w-4 h-4" style={{ color: '#5C63FF' }} />
                            Projektdetails
                            {request.videos?.length > 0 && (
                              <span className="text-xs text-gray-500">({request.videos.length} Video{request.videos.length > 1 ? '-Konfigs' : '-Konfig'})</span>
                            )}
                          </span>
                          {expandedRequests[request.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>

                        {expandedRequests[request.id] && (
                          <div className="mb-4 max-h-96 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
                            {renderVideoDetails(request)}
                          </div>
                        )}

                        <div className="flex gap-2 flex-wrap">
                          <button 
                            onClick={() => handleUpdateRequestStatus(request.id, 'reviewed')}
                            className="px-3 py-1.5 rounded-lg text-sm bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 flex items-center gap-1"
                          >
                            <Clock className="w-3 h-3" /> Geprüft
                          </button>
                          <button 
                            onClick={() => handleUpdateRequestStatus(request.id, 'accepted')}
                            className="px-3 py-1.5 rounded-lg text-sm bg-green-500/10 text-green-400 hover:bg-green-500/20 flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" /> Annehmen
                          </button>
                          <button 
                            onClick={() => handleUpdateRequestStatus(request.id, 'rejected')}
                            className="px-3 py-1.5 rounded-lg text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center gap-1"
                          >
                            <X className="w-3 h-3" /> Ablehnen
                          </button>
                          <a 
                            href={`mailto:${request.email}`}
                            className="px-3 py-1.5 rounded-lg text-sm bg-white/5 text-gray-300 hover:bg-white/10 flex items-center gap-1"
                          >
                            <Mail className="w-3 h-3" /> Kontaktieren
                          </a>
                          <button 
                            onClick={() => { setSelectedRequest(request); setShowAssignModal(true); }}
                            className="px-3 py-1.5 rounded-lg text-sm text-white hover:opacity-80 flex items-center gap-1"
                            style={{ backgroundColor: 'rgba(92,99,255,0.3)' }}
                          >
                            <Send className="w-3 h-3" /> Job zuweisen
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Assign Job Modal */}
      {showAssignModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl border border-white/10 p-6 w-full max-w-md" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Job zuweisen
              </h3>
              <button onClick={() => { setShowAssignModal(false); setSelectedRequest(null); }}>
                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-lg bg-white/5">
              <p className="font-bold">{selectedRequest.name}</p>
              <p className="text-sm text-gray-400">{selectedRequest.email}</p>
              <p className="text-purple-400 font-bold mt-2">€{selectedRequest.calculated_price?.toFixed(2) || '0'}</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Freelancer auswählen *</label>
                <select
                  value={selectedFreelancer}
                  onChange={(e) => setSelectedFreelancer(e.target.value)}
                  className="input-field"
                >
                  <option value="">-- Freelancer wählen --</option>
                  {freelancerUsers.map(f => (
                    <option key={f.id} value={f.id}>{f.name} ({f.specialization || 'Allgemein'})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Notizen (optional)</label>
                <textarea
                  value={assignNotes}
                  onChange={(e) => setAssignNotes(e.target.value)}
                  className="input-field"
                  rows={3}
                  placeholder="Hinweise für den Freelancer..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => { setShowAssignModal(false); setSelectedRequest(null); }}
                className="btn-secondary flex-1"
              >
                Abbrechen
              </button>
              <button 
                onClick={handleAssignJob}
                disabled={!selectedFreelancer}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                Zuweisen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Freelancer Account Modal */}
      {showCreateFreelancer && selectedApplication && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl border border-white/10 p-6 w-full max-w-md" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Freelancer Account erstellen
              </h3>
              <button onClick={() => { setShowCreateFreelancer(false); setSelectedApplication(null); setCreateAccountError(''); }}>
                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-lg bg-white/5">
              <p className="font-bold">{selectedApplication.name}</p>
              <p className="text-sm text-gray-400">{selectedApplication.email}</p>
              <p className="text-sm text-purple-400 mt-1">{selectedApplication.specialization}</p>
            </div>

            {createAccountError && (
              <div className="mb-4 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
                {createAccountError}
                {/* Show "Create Profile Only" button if email exists */}
                {createAccountError.includes('bereits registriert') && (
                  <button
                    onClick={handleCreateTeamProfile}
                    disabled={createAccountLoading}
                    className="mt-3 w-full py-2 px-4 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors flex items-center justify-center gap-2"
                  >
                    {createAccountLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
                        Wird erstellt...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        Nur Profil erstellen (Account existiert)
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Passwort für den Account *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={freelancerPassword}
                    onChange={(e) => setFreelancerPassword(e.target.value)}
                    className="input-field flex-1"
                    placeholder="Passwort eingeben..."
                  />
                  <button
                    type="button"
                    onClick={() => {
                      // Generate random password
                      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
                      let password = '';
                      for (let i = 0; i < 12; i++) {
                        password += chars.charAt(Math.floor(Math.random() * chars.length));
                      }
                      setFreelancerPassword(password);
                    }}
                    className="px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors whitespace-nowrap"
                  >
                    Generieren
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Dieses Passwort wird dem Freelancer mitgeteilt</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => { setShowCreateFreelancer(false); setSelectedApplication(null); setCreateAccountError(''); }}
                className="btn-secondary flex-1"
              >
                Abbrechen
              </button>
              <button 
                onClick={handleApproveApplication}
                disabled={!freelancerPassword || createAccountLoading}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {createAccountLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Account erstellen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceDetail && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="rounded-2xl border border-white/10 p-6 w-full max-w-2xl my-8" style={{ backgroundColor: '#1a1a1a' }}>
            {loadingInvoiceDetail ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#5C63FF', borderTopColor: 'transparent' }} />
                <p className="text-gray-400">Rechnung wird geladen...</p>
              </div>
            ) : selectedInvoice ? (
              <>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif', color: '#5C63FF' }}>
                      {selectedInvoice.invoice?.invoice_number || 'Entwurf'}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {selectedInvoice.invoice?.project_title || 'Video-Projekt'}
                    </p>
                  </div>
                  <button onClick={() => { setShowInvoiceDetail(false); setSelectedInvoice(null); }} data-testid="invoice-detail-close">
                    <X className="w-6 h-6 text-gray-400 hover:text-white" />
                  </button>
                </div>

                {/* Status Badge */}
                <div className="mb-5">
                  {selectedInvoice.invoice?.status === 'paid' ? (
                    <span className="px-3 py-1 rounded-full text-sm bg-green-500/20 text-green-400 font-medium">Bezahlt</span>
                  ) : selectedInvoice.invoice?.due_date && new Date(selectedInvoice.invoice.due_date) < new Date() ? (
                    <span className="px-3 py-1 rounded-full text-sm bg-red-500/20 text-red-400 font-medium">Überfällig</span>
                  ) : (
                    <span className="px-3 py-1 rounded-full text-sm bg-yellow-500/20 text-yellow-400 font-medium">Offen</span>
                  )}
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500 mb-1">Kunde</p>
                    <p className="font-medium">{selectedInvoice.invoice?.customer_name || '-'}</p>
                    <p className="text-sm text-gray-400">{selectedInvoice.invoice?.customer_email || '-'}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500 mb-1">Freelancer</p>
                    <p className="font-medium">{selectedInvoice.invoice?.freelancer_name || '-'}</p>
                    <p className="text-sm text-gray-400">{selectedInvoice.invoice?.freelancer_email || '-'}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500 mb-1">Rechnungsdatum</p>
                    <p className="font-medium">{selectedInvoice.invoice?.created_at ? new Date(selectedInvoice.invoice.created_at).toLocaleDateString('de-DE') : '-'}</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5">
                    <p className="text-xs text-gray-500 mb-1">Fälligkeitsdatum</p>
                    <p className="font-medium">{selectedInvoice.invoice?.due_date ? new Date(selectedInvoice.invoice.due_date).toLocaleDateString('de-DE') : '-'}</p>
                  </div>
                </div>

                {/* Amount Section */}
                <div className="rounded-lg border border-white/10 p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">Nettobetrag</span>
                    <span>{(selectedInvoice.invoice?.amount || 0).toFixed(2)}€</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">USt. ({selectedInvoice.company_settings?.tax_rate || 19}%)</span>
                    <span>{((selectedInvoice.invoice?.amount || 0) * (selectedInvoice.company_settings?.tax_rate || 19) / 100).toFixed(2)}€</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2 flex justify-between items-center">
                    <span className="font-bold">Gesamtbetrag</span>
                    <span className="text-xl font-bold" style={{ color: '#5C63FF' }}>
                      {((selectedInvoice.invoice?.amount || 0) * (1 + (selectedInvoice.company_settings?.tax_rate || 19) / 100)).toFixed(2)}€
                    </span>
                  </div>
                </div>

                {/* Project Details */}
                {selectedInvoice.invoice?.project_details && (
                  <div className="rounded-lg border border-white/10 p-4 mb-6">
                    <h4 className="text-sm font-bold text-gray-300 mb-3">Projektdetails</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {selectedInvoice.invoice.project_details.video_count && (
                        <div><span className="text-gray-500">Videos:</span> <span>{selectedInvoice.invoice.project_details.video_count}</span></div>
                      )}
                      {selectedInvoice.invoice.project_details.minutes_per_video && (
                        <div><span className="text-gray-500">Min/Video:</span> <span>{selectedInvoice.invoice.project_details.minutes_per_video}</span></div>
                      )}
                      {selectedInvoice.invoice.project_details.video_type && (
                        <div><span className="text-gray-500">Typ:</span> <span>{selectedInvoice.invoice.project_details.video_type}</span></div>
                      )}
                      {selectedInvoice.invoice.project_details.addons && Object.entries(selectedInvoice.invoice.project_details.addons).filter(([,v]) => v && v !== 'none').length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Addons:</span>
                          <span className="ml-2">
                            {Object.entries(selectedInvoice.invoice.project_details.addons)
                              .filter(([,v]) => v && v !== 'none' && v !== false)
                              .map(([k]) => k)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Company Info from Settings */}
                {selectedInvoice.company_settings && (
                  <div className="rounded-lg bg-white/5 p-4 mb-6">
                    <p className="text-xs text-gray-500 mb-1">Absender</p>
                    <p className="font-medium">{selectedInvoice.company_settings.company_name}</p>
                    <p className="text-sm text-gray-400">
                      {selectedInvoice.company_settings.company_address}, {selectedInvoice.company_settings.company_zip} {selectedInvoice.company_settings.company_city}
                    </p>
                    {selectedInvoice.company_settings.vat_id && (
                      <p className="text-xs text-gray-500 mt-1">USt-IdNr.: {selectedInvoice.company_settings.vat_id}</p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => handleDownloadPDF(selectedInvoice.invoice?.id, selectedInvoice.invoice?.invoice_number)}
                    className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                    style={{ backgroundColor: '#5C63FF' }}
                    data-testid="invoice-detail-pdf"
                  >
                    <Download className="w-4 h-4" /> PDF herunterladen
                  </button>
                  <button
                    onClick={() => handleDownloadXML(selectedInvoice.invoice?.id, selectedInvoice.invoice?.invoice_number)}
                    className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 transition-all"
                    data-testid="invoice-detail-xml"
                  >
                    <FileText className="w-4 h-4" style={{ color: '#5C63FF' }} /> XRechnung-XML
                  </button>
                  {selectedInvoice.invoice?.status !== 'paid' && (
                    <button
                      onClick={() => handleSendInvoiceToCustomer(selectedInvoice.invoice)}
                      className="flex-1 px-4 py-2.5 rounded-xl font-medium text-white flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 transition-all"
                      data-testid="invoice-detail-send"
                    >
                      <Send className="w-4 h-4" /> An Kunden senden
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">Rechnung nicht gefunden.</div>
            )}
          </div>
        </div>
      )}

      {/* Payment Link Modal */}
      {showPaymentModal && selectedJobForPayment && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="rounded-2xl border border-white/10 p-6 w-full max-w-md" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <CreditCard className="w-5 h-5 inline mr-2" />
                Rechnung erstellen
              </h3>
              <button onClick={() => { setShowPaymentModal(false); setSelectedJobForPayment(null); setGeneratedPaymentLink(null); }}>
                <X className="w-6 h-6 text-gray-400 hover:text-white" />
              </button>
            </div>

            <div className="mb-6 p-4 rounded-lg bg-white/5">
              <p className="font-bold">{selectedJobForPayment.customer_name}</p>
              <p className="text-sm text-gray-400">{selectedJobForPayment.customer_email}</p>
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-gray-500 text-sm">Betrag</p>
                <p className="text-2xl font-bold" style={{ color: '#5C63FF' }}>
                  €{selectedJobForPayment.project_details?.calculated_price?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>

            {!generatedPaymentLink ? (
              <button 
                onClick={handleCreatePaymentLink}
                disabled={paymentLoading}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {paymentLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Link className="w-4 h-4" />
                    Zahlungslink generieren
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-green-400 text-sm font-medium mb-2">✓ Zahlungslink erstellt!</p>
                  <a 
                    href={generatedPaymentLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-purple-400 hover:text-purple-300 break-all"
                  >
                    {generatedPaymentLink}
                  </a>
                </div>

                <div className="flex gap-3">
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(generatedPaymentLink);
                      alert('Link kopiert!');
                    }}
                    className="btn-secondary flex-1 flex items-center justify-center gap-2"
                  >
                    <Link className="w-4 h-4" />
                    Kopieren
                  </button>
                  <button 
                    onClick={handleSendInvoice}
                    disabled={paymentLoading}
                    className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {paymentLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Per E-Mail senden
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <button 
              onClick={() => { setShowPaymentModal(false); setSelectedJobForPayment(null); setGeneratedPaymentLink(null); }}
              className="w-full mt-4 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Schließen
            </button>
          </div>
        </div>
      )}
      
      {/* Chat Widget for Admin */}
      {isAuthenticated && <ChatWidget userId={adminUser?.id || 'admin'} userName="RevoCut Admin" userType="admin" />}
      
      {/* Toast notifications */}
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default AdminDashboard;
